import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  ChevronRight, ChevronDown, Lock, Plus, Trash2, Save, Loader2,
  Users, Wrench, TrendingUp, DollarSign, Settings2,
  X, Check, Download, Pencil, ArrowLeftRight, Flame, Gauge, Archive,
  Monitor, Briefcase, Scale, Heart, Megaphone, ShoppingCart, UserCheck, HandCoins,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import CategoryDriverSection from './CategoryDriverSection';

const API = '/api';

const EXPENSE_CAT_META = {
  A: 'Payroll & Benefits', B: 'SaaS & Tools', C: 'Professional Services',
  D: 'Marketing & Advertising', E: 'Office & Facilities', F: 'Travel & Entertainment',
  G: 'Hosting & Infrastructure', H: 'Insurance & Compliance', I: 'R&D', J: 'Other',
};
const DEFAULT_INCOME_CATS = ['Revenue', 'Deferred Revenue', 'Loan', 'Investment', 'Grant', 'Intercompany', 'Refund', 'Other'];
const SCENARIOS = ['Base', 'Conservative', 'Aggressive', 'Raise Now', 'Raise Later'];

// Expense category driver sections (after Staffing, before Income/Investments)
const EXPENSE_DRIVER_SECTIONS = [
  { key: 'digital_tools', label: 'Digital Tools', icon: Monitor, categoryMatch: ['SaaS & Tools', 'SaaS', 'Digital Tools', 'B. Digital Tools', 'B'] },
  { key: 'operations', label: 'Operations', icon: Settings2, categoryMatch: ['Operations', 'Office & Facilities', 'E. Office & Facilities', 'E'] },
  { key: 'professional_services', label: 'Professional Services', icon: Briefcase, categoryMatch: ['Professional Services', 'C. Professional Services', 'C'] },
  { key: 'taxes_compliance', label: 'Taxes & Compliance', icon: Scale, categoryMatch: ['Taxes and Compliance', 'Insurance & Compliance', 'Taxes & Compliance', 'H. Insurance & Compliance', 'H'] },
  { key: 'hr', label: 'HR', icon: Heart, categoryMatch: ['HR', 'Human Resources'] },
  { key: 'marketing_advertising', label: 'Marketing & Advertising', icon: Megaphone, categoryMatch: ['Marketing & Advertising', 'Marketing', 'D. Marketing & Advertising', 'D'] },
  { key: 'sales', label: 'Sales', icon: ShoppingCart, categoryMatch: ['Sales'] },
  { key: 'account_management', label: 'Account Management', icon: UserCheck, categoryMatch: ['Account Management'] },
  { key: 'fundraising_expense', label: 'Fundraising', icon: HandCoins, categoryMatch: ['Fundraising'] },
];

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  if (n === 0) return '-';
  const s = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(1)}K`;
  return `${s}$${a.toFixed(0)}`;
};

// Natural sort: "1. X", "2. Y", "10. Z" → 1, 2, 10 (not 1, 10, 2)
const naturalCompare = (a, b) => {
  const ax = a.match(/^(\d+)/);
  const bx = b.match(/^(\d+)/);
  if (ax && bx) {
    const diff = parseInt(ax[1], 10) - parseInt(bx[1], 10);
    if (diff !== 0) return diff;
  }
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};
const fmtMoneyFull = (v) => (Number(v) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function genBuckets(startYm, endYm, grouping) {
  const b = [];
  if (grouping === 'monthly') {
    let [y, m] = startYm.split('-').map(Number);
    const [ey, em] = endYm.split('-').map(Number);
    while (y < ey || (y === ey && m <= em)) {
      b.push(`${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}`);
      m++; if (m > 12) { m = 1; y++; }
    }
  } else if (grouping === 'quarterly') {
    let [y] = startYm.split('-').map(Number);
    let q = Math.ceil(Number(startYm.split('-')[1]) / 3);
    const [ey] = endYm.split('-').map(Number);
    const eq = Math.ceil(Number(endYm.split('-')[1]) / 3);
    while (y < ey || (y === ey && q <= eq)) {
      b.push(`${y}-Q${q}`); q++; if (q > 4) { q = 1; y++; }
    }
  } else if (grouping === 'weekly') {
    let dt = new Date(`${startYm}-01T00:00:00`);
    dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
    const end = new Date(`${endYm}-28T00:00:00`);
    while (dt <= end) {
      const d2 = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
      d2.setUTCDate(d2.getUTCDate() + 4 - (d2.getUTCDay() || 7));
      const ys = new Date(Date.UTC(d2.getUTCFullYear(), 0, 1));
      const wk = Math.ceil((((d2 - ys) / 864e5) + 1) / 7);
      b.push(`${d2.getUTCFullYear()}-W${String(wk).padStart(2, '0')}`);
      dt.setDate(dt.getDate() + 7);
    }
  }
  return b;
}

function bucketLabel(bk, g) {
  if (g === 'monthly') {
    const mons = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const [y, m] = bk.split('-');
    return `${mons[parseInt(m) - 1]} ${y.slice(2)}`;
  }
  return bk;
}

function isHistorical(bk, cutoff, g) {
  if (!cutoff) return true;
  const co = cutoff.slice(0, 7);
  if (g === 'monthly') return bk <= co;
  if (g === 'quarterly') {
    const [cy, cm] = cutoff.split('-').map(Number);
    return bk <= `${cy}-Q${Math.ceil(cm / 3)}`;
  }
  if (g === 'weekly') {
    const d = new Date(cutoff);
    const d2 = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    d2.setUTCDate(d2.getUTCDate() + 4 - (d2.getUTCDay() || 7));
    const ys = new Date(Date.UTC(d2.getUTCFullYear(), 0, 1));
    const wk = Math.ceil((((d2 - ys) / 864e5) + 1) / 7);
    return bk <= `${d2.getUTCFullYear()}-W${String(wk).padStart(2, '0')}`;
  }
  return true;
}

function dateToBucket(ds, g) {
  if (!ds) return '';
  try {
    const d = new Date(ds);
    if (g === 'monthly') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (g === 'quarterly') return `${d.getFullYear()}-Q${Math.ceil((d.getMonth() + 1) / 3)}`;
    if (g === 'weekly') {
      const d2 = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      d2.setUTCDate(d2.getUTCDate() + 4 - (d2.getUTCDay() || 7));
      const ys = new Date(Date.UTC(d2.getUTCFullYear(), 0, 1));
      const wk = Math.ceil((((d2 - ys) / 864e5) + 1) / 7);
      return `${d2.getUTCFullYear()}-W${String(wk).padStart(2, '0')}`;
    }
  } catch { /* noop */ }
  return '';
}

function bucketToDateObj(bk, g) {
  if (g === 'monthly') return new Date(`${bk}-01T00:00:00`);
  if (g === 'quarterly') {
    const [y, qs] = bk.split('-Q');
    return new Date(`${y}-${String((parseInt(qs) - 1) * 3 + 1).padStart(2, '0')}-01T00:00:00`);
  }
  return new Date();
}

// ==================== MAIN COMPONENT ====================
export default function PLForecastTab({ workspaceId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plData, setPlData] = useState(null);
  const [grouping, setGrouping] = useState('monthly');
  const [cutoffDate, setCutoffDate] = useState(new Date().toISOString().slice(0, 10));
  const [startYm, setStartYm] = useState(`${new Date().getFullYear()}-01`);
  const [endYm, setEndYm] = useState(`${new Date().getFullYear() + 1}-12`);
  const [expanded, setExpanded] = useState(new Set());
  const [forecastCells, setForecastCells] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [scenario, setScenario] = useState('Base');
  const [drivers, setDrivers] = useState([]);
  const [driverTab, setDriverTab] = useState('staffing');
  const [driverModal, setDriverModal] = useState(false);
  const [driverForm, setDriverForm] = useState({});
  const [editingDriver, setEditingDriver] = useState(null);
  const [addCatModal, setAddCatModal] = useState(null);
  const [newCatName, setNewCatName] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [compareScenario, setCompareScenario] = useState('Conservative');
  const [predictedVsActual, setPredictedVsActual] = useState(false);
  const [staffingActuals, setStaffingActuals] = useState({ staff: [], by_category: {} });
  const [categoryItemActuals, setCategoryItemActuals] = useState({});
  const [refVendors, setRefVendors] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const saveTimer = useRef(null);

  // ==================== FETCH ====================
  const fetchData = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/finance/pl/${workspaceId}/data?grouping=${grouping}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setPlData(d);
        if (d.config?.cutoff_date) setCutoffDate(d.config.cutoff_date);
        if (d.config?.start_ym) setStartYm(d.config.start_ym);
        if (d.config?.end_ym) setEndYm(d.config.end_ym);
        if (d.config?.grouping) setGrouping(d.config.grouping);
        const cm = {};
        (d.forecast_cells || []).forEach(c => {
          cm[`${c.category_type}|${c.category_id}|${c.time_bucket}`] = c.value;
        });
        setForecastCells(cm);
        setDrivers(d.drivers || []);
        setStaffingActuals(d.staffing_actuals || { staff: [], by_category: {} });
        setCategoryItemActuals(d.category_item_actuals || {});
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [workspaceId, grouping]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch org members for staffing people picker
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/org/members`, { credentials: 'include' });
        if (r.ok) setOrgMembers(await r.json());
      } catch {}
      // Fetch vendors for bidirectional sync
      if (workspaceId) {
        try {
          const r = await fetch(`${API}/finance/ref/${workspaceId}/vendors`, { credentials: 'include' });
          if (r.ok) setRefVendors(await r.json());
        } catch {}
      }
    })();
  }, [workspaceId]);

  // ==================== CONFIG ====================
  const saveConfig = useCallback(async (ov = {}) => {
    const cfg = { cutoff_date: cutoffDate, start_ym: startYm, end_ym: endYm, grouping, ...ov };
    try {
      await fetch(`${API}/finance/pl/${workspaceId}/config`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(cfg),
      });
    } catch { /* noop */ }
  }, [workspaceId, cutoffDate, startYm, endYm, grouping]);

  // ==================== CELL EDIT ====================
  const ck = (t, c, b) => `${t}|${c}|${b}`;
  // Cell editing removed — P/L table is now read-only. All edits happen via Forecast Drivers.
  const startEdit = () => {}; // kept as no-op for any remaining refs
  const saveEdit = () => {};
  const cancelEdit = () => {};

  const flushDirty = useCallback(async () => {
    if (dirty.size === 0) return;
    setSaving(true);
    const cells = [...dirty].map(k => {
      const [category_type, category_id, time_bucket] = k.split('|');
      return { category_type, category_id, time_bucket, value: forecastCells[k] || 0 };
    });
    try {
      await fetch(`${API}/finance/pl/${workspaceId}/forecast-cells`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ cells }),
      });
      setDirty(new Set());
      toast.success('Forecast saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  }, [dirty, forecastCells, workspaceId]);

  // ==================== DRIVERS ====================
  const saveDriverFn = async () => {
    const body = { ...driverForm, driver_type: driverTab };
    if (editingDriver) body.driver_id = editingDriver.driver_id;
    try {
      const r = await fetch(`${API}/finance/pl/${workspaceId}/drivers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
      });
      if (r.ok) { toast.success(editingDriver ? 'Updated' : 'Added'); setDriverModal(false); setDriverForm({}); setEditingDriver(null); fetchData(); }
    } catch { toast.error('Failed'); }
  };
  const deleteDriverFn = async (id) => {
    if (!window.confirm('Delete this driver?')) return;
    await fetch(`${API}/finance/pl/${workspaceId}/drivers/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchData(); toast.success('Deleted');
  };

  // ==================== CUSTOM CATEGORIES ====================
  const addCustomCategory = async (type) => {
    if (!newCatName.trim()) return;
    await fetch(`${API}/finance/pl/${workspaceId}/custom-categories`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ action: 'add', type, name: newCatName.trim(), id: newCatName.trim() }),
    });
    // Sync new expense categories to the reference data table
    if (type === 'expense') {
      try {
        await fetch(`${API}/finance/ref/${workspaceId}/expense-categories`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ name: newCatName.trim(), description: 'Synced from P/L Forecast' }),
        });
      } catch {}
    }
    setAddCatModal(null); setNewCatName(''); fetchData(); toast.success('Category added');
  };
  const deleteCustomCategory = async (type, id) => {
    if (!window.confirm('Delete this forecast category?')) return;
    await fetch(`${API}/finance/pl/${workspaceId}/custom-categories`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ action: 'delete', type, id }),
    });
    fetchData(); toast.success('Category removed');
  };

  // ==================== CSV EXPORT ====================
  const exportCSV = () => {
    const url = `${API}/finance/pl/${workspaceId}/export-csv?grouping=${grouping}`;
    const a = document.createElement('a');
    a.href = url; a.download = 'pl_forecast.csv'; a.click();
  };

  // ==================== COMPUTED ====================
  const buckets = useMemo(() => genBuckets(startYm, endYm, grouping), [startYm, endYm, grouping]);
  const hist = useMemo(() => plData?.historical || { expenses: {}, income: {} }, [plData]);
  const cutoffIdx = useMemo(() => {
    for (let i = buckets.length - 1; i >= 0; i--) {
      if (isHistorical(buckets[i], cutoffDate, grouping)) return i;
    }
    return -1;
  }, [buckets, cutoffDate, grouping]);

  // Dynamic categories from actual data
  // Build a code lookup from expense-categories ref data
  const catCodeMap = useMemo(() => {
    const map = {};
    for (const c of (plData?.expense_categories || [])) {
      if (c.name) map[c.name] = c.code || '';
      if (c.code) map[c.code] = c.code;
    }
    for (const c of (plData?.income_categories || [])) {
      if (c.name) map[c.name] = c.code || '';
      if (c.code) map[c.code] = c.code;
    }
    return map;
  }, [plData]);

  const expCats = useMemo(() => {
    const cats = [];
    const seen = new Set();
    Object.keys(hist.expenses).sort(naturalCompare).forEach(k => {
      seen.add(k);
      const subObjs = Object.keys(hist.expenses[k]?.subs || {}).map(s => ({ name: s, code: catCodeMap[s] || '' }));
      subObjs.sort((a, b) => naturalCompare(a.code || a.name, b.code || b.name));
      const code = catCodeMap[k] || '';
      cats.push({ id: k, name: k, code, isForecastOnly: false, subs: subObjs });
    });
    (plData?.config?.custom_expense_categories || []).forEach(c => {
      if (!seen.has(c.id)) { seen.add(c.id); cats.push({ ...c, code: c.code || catCodeMap[c.id] || '', subs: [] }); }
    });
    if (cats.length === 0) {
      Object.entries(EXPENSE_CAT_META).forEach(([code, name]) => {
        cats.push({ id: code, name: `${code}: ${name}`, code, isForecastOnly: false, subs: [] });
      });
    }
    cats.sort((a, b) => naturalCompare(a.code || a.id, b.code || b.id));
    return cats;
  }, [hist, plData, catCodeMap]);

  const incCats = useMemo(() => {
    const cats = [];
    const seen = new Set();
    Object.keys(hist.income).sort(naturalCompare).forEach(k => {
      seen.add(k);
      const subObjs = Object.keys(hist.income[k]?.subs || {}).map(s => ({ name: s, code: catCodeMap[s] || '' }));
      subObjs.sort((a, b) => naturalCompare(a.code || a.name, b.code || b.name));
      const code = catCodeMap[k] || '';
      cats.push({ id: k, name: k, code, isForecastOnly: false, subs: subObjs });
    });
    (plData?.config?.custom_income_categories || []).forEach(c => {
      if (!seen.has(c.id)) { seen.add(c.id); cats.push({ ...c, code: c.code || catCodeMap[c.id] || '', subs: [] }); }
    });
    if (cats.length === 0) DEFAULT_INCOME_CATS.forEach(n => cats.push({ id: n, name: n, code: '', isForecastOnly: false, subs: [] }));
    cats.sort((a, b) => naturalCompare(a.code || a.id, b.code || b.id));
    return cats;
  }, [hist, plData, catCodeMap]);

  // Driver contributions per bucket - parameterized by scenario
  const computeDriverContrib = useCallback((scenarioName) => {
    const cx = { expense: {}, income: {} };
    const sn = scenarioName.toLowerCase();
    (drivers || []).forEach(d => {
      if (d.scenario && d.scenario !== 'base' && d.scenario !== sn) return;

      if (d.driver_type === 'staffing') {
        const mc = (d.base_salary || 0) / 12 * (1 + (d.benefits_pct || 0) / 100);
        if (mc <= 0) return;
        const cl = d.category_link || 'A. Staff';
        buckets.forEach((b, i) => {
          if (i <= cutoffIdx) return;
          const bd = bucketToDateObj(b, grouping);
          const ds = d.start_date ? new Date(d.start_date) : new Date('2000-01-01');
          const de = d.end_date ? new Date(d.end_date) : new Date('2100-01-01');
          if (bd >= ds && bd <= de) {
            const k = `expense|${cl}|${b}`;
            cx.expense[k] = (cx.expense[k] || 0) + mc;
          }
        });
      } else if (d.driver_type === 'tools' || EXPENSE_DRIVER_SECTIONS.some(s => s.key === d.driver_type)) {
        let c = d.monthly_cost || 0;
        if (c <= 0) return;
        // For per_person rules, multiply by headcount
        if (d.forecast_rule === 'per_person_all') {
          const staffCount = (staffingActuals?.staff || []).filter(s => !s.is_archived).length;
          c = c * (staffCount || 1);
        }
        const cl = d.category_link || d.tool_category || d.sub_type || 'B. Digital Tools';
        buckets.forEach((b, i) => {
          if (i <= cutoffIdx) return;
          const bd = bucketToDateObj(b, grouping);
          const ds = d.start_date ? new Date(d.start_date) : new Date('2000-01-01');
          const de = d.end_date ? new Date(d.end_date) : new Date('2100-01-01');
          if (bd >= ds && bd <= de) {
            const k = `expense|${cl}|${b}`;
            cx.expense[k] = (cx.expense[k] || 0) + c;
            c *= (1 + (d.growth_pct || 0) / 100);
          }
        });
      } else if (d.driver_type === 'investment') {
        if (!d.expected_close_date || !d.expected_amount) return;
        const cb = dateToBucket(d.expected_close_date, grouping);
        const cl = d.category_link || '12. Investment';
        const k = `income|${cl}|${cb}`;
        cx.income[k] = (cx.income[k] || 0) + (d.expected_amount || 0) * (d.fx_rate || 1);
      } else if (d.driver_type === 'income_projection') {
        let customers = d.starting_customers || 0;
        const newPerMo = d.monthly_new_customers || 0;
        const churn = (d.churn_pct || 0) / 100;
        const price = d.price_per_customer || 0;
        const lag = Math.round(d.sales_lag || 0);
        const cl = d.income_category || d.category_link || 'Revenue';
        const revenueQueue = [];
        buckets.forEach((b, i) => {
          if (i <= cutoffIdx) return;
          customers = customers * (1 - churn) + newPerMo;
          revenueQueue.push({ bucket: b, rev: customers * price });
        });
        revenueQueue.forEach((item, idx) => {
          const targetIdx = idx + lag;
          if (targetIdx < revenueQueue.length) {
            const tb = revenueQueue[targetIdx].bucket;
            const k = `income|${cl}|${tb}`;
            cx.income[k] = (cx.income[k] || 0) + item.rev;
          }
        });
      }
    });
    return cx;
  }, [drivers, buckets, cutoffIdx, grouping]);

  const driverContrib = useMemo(() => computeDriverContrib(scenario), [computeDriverContrib, scenario]);
  const compareContrib = useMemo(() => compareMode ? computeDriverContrib(compareScenario) : { expense: {}, income: {} }, [compareMode, computeDriverContrib, compareScenario]);

  // Compute a single driver's contribution per bucket (for the expanded driver grid)
  const computeDriverRow = useCallback((d, bkts) => {
    const vals = bkts.map(() => 0);
    if (d.driver_type === 'staffing') {
      const mc = (d.base_salary || 0) / 12 * (1 + (d.benefits_pct || 0) / 100);
      if (mc <= 0) return vals;
      bkts.forEach((b, i) => {
        const bd = bucketToDateObj(b, grouping);
        const ds = d.start_date ? new Date(d.start_date) : new Date('2000-01-01');
        const de = d.end_date ? new Date(d.end_date) : new Date('2100-01-01');
        if (bd >= ds && bd <= de) vals[i] = mc;
      });
    } else if (d.driver_type === 'tools' || EXPENSE_DRIVER_SECTIONS.some(s => s.key === d.driver_type)) {
      let c = d.monthly_cost || 0;
      if (c <= 0) return vals;
      if (d.forecast_rule === 'per_person_all') {
        const staffCount = (staffingActuals?.staff || []).filter(s => !s.is_archived).length;
        c = c * (staffCount || 1);
      }
      bkts.forEach((b, i) => {
        const bd = bucketToDateObj(b, grouping);
        const ds = d.start_date ? new Date(d.start_date) : new Date('2000-01-01');
        const de = d.end_date ? new Date(d.end_date) : new Date('2100-01-01');
        if (bd >= ds && bd <= de) { vals[i] = c; c *= (1 + (d.growth_pct || 0) / 100); }
      });
    } else if (d.driver_type === 'investment') {
      if (!d.expected_close_date || !d.expected_amount) return vals;
      const cb = dateToBucket(d.expected_close_date, grouping);
      const idx = bkts.indexOf(cb);
      if (idx >= 0) vals[idx] = (d.expected_amount || 0) * (d.fx_rate || 1);
    } else if (d.driver_type === 'income_projection') {
      let customers = d.starting_customers || 0;
      const newPerMo = d.monthly_new_customers || 0;
      const churn = (d.churn_pct || 0) / 100;
      const price = d.price_per_customer || 0;
      const lag = Math.round(d.sales_lag || 0);
      const revQueue = [];
      bkts.forEach((b, i) => {
        customers = customers * (1 - churn) + newPerMo;
        revQueue.push({ idx: i, rev: customers * price });
      });
      revQueue.forEach((item, ri) => {
        const ti = ri + lag;
        if (ti < bkts.length) vals[ti] += item.rev;
      });
    }
    return vals;
  }, [grouping]);

  // Cell value: historical or forecast+driver
  const cellVal = useCallback((type, catId, bucket, bIdx) => {
    if (bIdx <= cutoffIdx) {
      const sec = type === 'expense' ? hist.expenses : hist.income;
      return (sec[catId]?.totals?.[bucket]) || 0;
    }
    const manual = forecastCells[ck(type, catId, bucket)] || 0;
    const drv = (type === 'expense' ? driverContrib.expense : driverContrib.income)[`${type}|${catId}|${bucket}`] || 0;
    return manual + drv;
  }, [cutoffIdx, hist, forecastCells, driverContrib]);

  const subCellVal = useCallback((type, catId, subId, bucket, bIdx) => {
    if (bIdx <= cutoffIdx) {
      const sec = type === 'expense' ? hist.expenses : hist.income;
      return (sec[catId]?.subs?.[subId]?.[bucket]) || 0;
    }
    return 0; // subcategory forecast not individually tracked
  }, [cutoffIdx, hist]);

  // Totals
  const totals = useMemo(() => {
    const et = buckets.map((b, i) => expCats.reduce((s, c) => s + cellVal('expense', c.id, b, i), 0));
    const it = buckets.map((b, i) => incCats.reduce((s, c) => s + cellVal('income', c.id, b, i), 0));
    let ce = 0, ci = 0;
    const ceArr = et.map(v => { ce += v; return ce; });
    const ciArr = it.map(v => { ci += v; return ci; });
    const pl = buckets.map((_, i) => it[i] - et[i]);
    const rb = buckets.map((_, i) => ciArr[i] - ceArr[i]);
    return { et, it, ceArr, ciArr, pl, rb };
  }, [buckets, expCats, incCats, cellVal]);

  // Comparison scenario cell value
  const compareCellVal = useCallback((type, catId, bucket, bIdx) => {
    if (bIdx <= cutoffIdx) return cellVal(type, catId, bucket, bIdx); // historical same
    const manual = forecastCells[ck(type, catId, bucket)] || 0;
    const drv = (type === 'expense' ? compareContrib.expense : compareContrib.income)[`${type}|${catId}|${bucket}`] || 0;
    return manual + drv;
  }, [cutoffIdx, cellVal, forecastCells, compareContrib]);

  const compareTotals = useMemo(() => {
    if (!compareMode) return null;
    const et = buckets.map((b, i) => expCats.reduce((s, c) => s + compareCellVal('expense', c.id, b, i), 0));
    const it = buckets.map((b, i) => incCats.reduce((s, c) => s + compareCellVal('income', c.id, b, i), 0));
    let ce = 0, ci = 0;
    const ceArr = et.map(v => { ce += v; return ce; });
    const ciArr = it.map(v => { ci += v; return ci; });
    const pl = buckets.map((_, i) => it[i] - et[i]);
    const rb = buckets.map((_, i) => ciArr[i] - ceArr[i]);
    return { et, it, ceArr, ciArr, pl, rb };
  }, [compareMode, buckets, expCats, incCats, compareCellVal]);

  // Burn rate & runway - ALWAYS monthly, decoupled from table grouping
  // First 3 tiles (avgExp, avgInc, netBurn) use raw monthly ledger data
  // Last 2 (cashPosition, runway) derived from the running balance in the table
  const burnMetrics = useMemo(() => {
    if (!plData) return null;
    const monthlyHist = plData?.monthly_historical || plData?.historical || { expenses: {}, income: {} };
    // Generate monthly buckets from start to cutoff
    const monthlyBuckets = genBuckets(startYm, endYm, 'monthly');
    const cutoffMonth = cutoffDate.slice(0, 7);
    const monthCutoffIdx = monthlyBuckets.findIndex(b => b > cutoffMonth) - 1;
    const effectiveCutoff = monthCutoffIdx >= 0 ? monthCutoffIdx : monthlyBuckets.length - 1;

    // Average from last 3 historical months using monthly data
    const lookback = Math.min(3, effectiveCutoff + 1);
    if (lookback <= 0) return null;
    let totalExp = 0, totalInc = 0;
    for (let i = effectiveCutoff; i > effectiveCutoff - lookback; i--) {
      const b = monthlyBuckets[i];
      if (!b) continue;
      // Sum all expense categories for this month
      for (const catKey of Object.keys(monthlyHist.expenses || {})) {
        totalExp += (monthlyHist.expenses[catKey]?.totals?.[b]) || 0;
      }
      // Sum all income categories for this month
      for (const catKey of Object.keys(monthlyHist.income || {})) {
        totalInc += (monthlyHist.income[catKey]?.totals?.[b]) || 0;
      }
    }
    const avgExp = totalExp / lookback;
    const avgInc = totalInc / lookback;
    const netBurn = avgExp - avgInc;

    // Cash position and runway from the table's running balance at cutoff
    const cashPosition = cutoffIdx >= 0 ? (totals.rb[cutoffIdx] || 0) : 0;
    const runway = netBurn > 0 ? cashPosition / netBurn : null;
    return { avgExp, avgInc, netBurn, cashPosition, runway };
  }, [plData, startYm, endYm, cutoffDate, cutoffIdx, totals]);

  const toggle = (id) => setExpanded(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const hasDrv = (t, c, b) => {
    const k = `${t}|${c}|${b}`;
    return (t === 'expense' ? driverContrib.expense[k] : driverContrib.income[k]) > 0;
  };

  const drvTooltip = (t, c, b) => (drivers || []).filter(d => {
    if (t === 'expense' && d.driver_type === 'staffing' && (d.category_link || 'A. Staff') === c) return true;
    if (t === 'expense' && (d.driver_type === 'tools' || EXPENSE_DRIVER_SECTIONS.some(s => s.key === d.driver_type)) && (d.category_link || d.tool_category || d.sub_type || 'B. Digital Tools') === c) return true;
    if (t === 'income' && d.driver_type === 'investment' && (d.category_link || '12. Investment') === c) {
      return dateToBucket(d.expected_close_date, grouping) === b;
    }
    if (t === 'income' && d.driver_type === 'income_projection' && (d.income_category || d.category_link || 'Revenue') === c) return true;
    return false;
  }).map(d => {
    if (d.driver_type === 'staffing') return `${d.role || d.name} – ${fmtMoney(d.base_salary)}/yr`;
    if (d.driver_type === 'tools') return `${d.tool_name || d.name} – ${fmtMoney(d.monthly_cost)}/mo`;
    if (d.driver_type === 'investment') return `${d.investor_name || d.name} – ${fmtMoney(d.expected_amount)} – ${d.status}`;
    if (d.driver_type === 'income_projection') return `${d.name} – ${d.starting_customers} cust @ ${fmtMoney(d.price_per_customer)}/mo`;
    return d.name;
  });

  // Cell breakdown for tooltip — shows sub-categories that make up the sum
  const cellBreakdown = useCallback((type, catId, bucket, bIdx) => {
    const sec = type === 'expense' ? hist.expenses : hist.income;
    const catData = sec[catId];
    if (!catData) return [];
    const subs = catData.subs || {};
    const items = [];
    for (const [subName, subBuckets] of Object.entries(subs)) {
      const val = subBuckets[bucket] || 0;
      if (val !== 0) items.push({ name: subName, value: val });
    }
    // Also check driver contributions for forecast buckets
    if (bIdx > cutoffIdx) {
      const drvKey = `${type}|${catId}|${bucket}`;
      const drvVal = (type === 'expense' ? driverContrib.expense[drvKey] : driverContrib.income[drvKey]) || 0;
      if (drvVal > 0) items.push({ name: 'Forecast drivers', value: drvVal });
      const manualVal = forecastCells[ck(type, catId, bucket)] || 0;
      if (manualVal > 0) items.push({ name: 'Manual forecast', value: manualVal });
    }
    items.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    return items;
  }, [hist, cutoffIdx, driverContrib, forecastCells]);

  // Predicted vs actual data — for each bucket, show forecast amount, actual amount, variance %
  const getPredictedVsActualData = useCallback((type, catId, bucket, bIdx) => {
    if (bIdx > cutoffIdx) return null; // Only for historical buckets where we have actuals
    const actual = cellVal(type, catId, bucket, bIdx);
    // Forecast = driver contributions + manual forecast cells for this bucket
    const drvKey = `${type}|${catId}|${bucket}`;
    const drvVal = (type === 'expense' ? driverContrib.expense[drvKey] : driverContrib.income[drvKey]) || 0;
    const manualVal = forecastCells[ck(type, catId, bucket)] || 0;
    const forecast = drvVal + manualVal;
    if (forecast === 0 && actual === 0) return null;
    const variance = forecast !== 0 ? ((actual - forecast) / forecast) * 100 : (actual !== 0 ? 100 : 0);
    return { forecast, actual, variance };
  }, [cellVal, cutoffIdx, driverContrib, forecastCells]);

  // ==================== RENDER ====================
  if (loading) return <div className="flex items-center justify-center h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  const renderCell = (type, catId, bucket, bIdx) => {
    const hist_ = bIdx <= cutoffIdx;
    const val = cellVal(type, catId, bucket, bIdx);
    const hasD = hasDrv(type, catId, bucket);
    const isCut = bIdx === cutoffIdx;
    const breakdown = cellBreakdown(type, catId, bucket, bIdx);
    const pvaData = predictedVsActual ? getPredictedVsActualData(type, catId, bucket, bIdx) : null;

    const cellContent = (
      <td key={bucket} className={cn(
        "p-2 text-right font-mono text-xs transition-colors",
        hist_ ? "bg-transparent" : "bg-blue-50/30 dark:bg-blue-950/10",
        isCut && "border-r-2 border-amber-500",
        val === 0 && "text-muted-foreground/40"
      )}>
        <span className="inline-flex items-center gap-0.5">
          {hasD && <Lock className="w-2.5 h-2.5 text-amber-500 inline" />}
          {fmtMoney(val)}
        </span>
        {predictedVsActual && pvaData && (
          <div className="mt-0.5 space-y-0">
            <span className="block text-[8px] text-muted-foreground">F: {fmtMoney(pvaData.forecast)}</span>
            <span className={cn("block text-[8px] font-semibold",
              Math.abs(pvaData.variance) < 5 ? "text-emerald-500" :
              Math.abs(pvaData.variance) < 15 ? "text-amber-500" : "text-red-500"
            )}>{pvaData.variance > 0 ? '+' : ''}{pvaData.variance.toFixed(0)}%</span>
          </div>
        )}
        {compareMode && !hist_ && (() => {
          const compVal = compareCellVal(type, catId, bucket, bIdx);
          const delta = compVal - val;
          if (Math.abs(delta) < 0.01) return null;
          const isGood = type === 'expense' ? delta < 0 : delta > 0;
          return <span className={cn("block text-[9px] mt-0.5", isGood ? "text-emerald-500" : "text-red-400")}>{delta > 0 ? '+' : ''}{fmtMoney(delta)}</span>;
        })()}
      </td>
    );

    // Build comprehensive tooltip content
    const tooltipLines = [];
    if (breakdown.length > 0) {
      breakdown.forEach(item => tooltipLines.push({ label: item.name, value: item.value }));
    }
    if (hasD) {
      const tips = drvTooltip(type, catId, bucket);
      tips.forEach(t => tooltipLines.push({ label: t, value: null }));
    }
    // For forecast cells with no breakdown, show what makes up the value
    if (bIdx > cutoffIdx && tooltipLines.length === 0 && val !== 0) {
      const drvKey = `${type}|${catId}|${bucket}`;
      const drvVal = (type === 'expense' ? driverContrib.expense[drvKey] : driverContrib.income[drvKey]) || 0;
      const manualVal = forecastCells[ck(type, catId, bucket)] || 0;
      if (drvVal > 0) tooltipLines.push({ label: 'Driver contributions', value: drvVal });
      if (manualVal > 0) tooltipLines.push({ label: 'Manual override', value: manualVal });
    }
    // For historical cells with value but no subs
    if (bIdx <= cutoffIdx && tooltipLines.length === 0 && val !== 0) {
      tooltipLines.push({ label: 'Direct entry (no sub-categories)', value: val });
    }

    // Wrap in tooltip if there's any content
    if (tooltipLines.length > 0) {
      return (
        <TooltipProvider key={bucket} delayDuration={200}>
          <UITooltip>
            <TooltipTrigger asChild>{cellContent}</TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <div className="text-xs space-y-0.5">
                <p className="font-semibold mb-1">{bIdx <= cutoffIdx ? 'Actual' : 'Forecast'} Breakdown ({fmtMoneyFull(val)}):</p>
                {tooltipLines.map((item, i) => (
                  <p key={i} className="flex justify-between gap-4">
                    <span className="text-muted-foreground">{item.label}</span>
                    {item.value !== null && <span className="font-mono">{fmtMoneyFull(item.value)}</span>}
                  </p>
                ))}
              </div>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      );
    }
    return cellContent;
  };

  const renderSubCell = (type, catId, subId, bucket, bIdx) => {
    const val = subCellVal(type, catId, subId, bucket, bIdx);
    const isCut = bIdx === cutoffIdx;
    return (
      <td key={bucket} className={cn("p-1.5 text-right font-mono text-[10px] text-muted-foreground",
        bIdx <= cutoffIdx ? "bg-transparent" : "bg-blue-50/20 dark:bg-blue-950/5",
        isCut && "border-r-2 border-amber-500",
        val === 0 && "text-muted-foreground/30"
      )}>{fmtMoney(val)}</td>
    );
  };

  return (
    <div className="space-y-4" data-testid="pl-forecast-tab">
      {/* BURN RATE & RUNWAY */}
      {burnMetrics && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="burn-metrics">
          <BurnCard label="Avg Monthly Expense" value={fmtMoney(burnMetrics.avgExp)} color="text-red-500" sub="Always monthly" />
          <BurnCard label="Avg Monthly Income" value={fmtMoney(burnMetrics.avgInc)} color="text-emerald-500" sub="Always monthly" />
          <BurnCard label="Net Monthly Burn" value={fmtMoney(burnMetrics.netBurn)}
            color={burnMetrics.netBurn > 0 ? "text-red-500" : "text-emerald-500"}
            sub={burnMetrics.netBurn > 0 ? "Burning cash" : "Cash positive"} />
          <BurnCard label="Cash Position" value={fmtMoney(burnMetrics.cashPosition)}
            color={burnMetrics.cashPosition >= 0 ? "text-emerald-500" : "text-red-500"} sub="From table balance" />
          <BurnCard label="Runway"
            value={burnMetrics.runway !== null ? `${Math.round(burnMetrics.runway)} mo` : 'Cash generative'}
            color={burnMetrics.runway !== null && burnMetrics.runway < 6 ? "text-red-500" : "text-emerald-500"}
            sub={burnMetrics.runway === null ? "Not burning cash" : burnMetrics.runway < 6 ? "Critical" : burnMetrics.runway < 12 ? "Watch" : "Healthy"} />
        </div>
      )}

      {/* CONTROLS */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="pl-controls">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground font-medium">Grouping</span>
          <Select value={grouping} onValueChange={v => { setGrouping(v); saveConfig({ grouping: v }); }}>
            <SelectTrigger className="w-28 h-8 text-xs" data-testid="pl-grouping-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground font-medium">Cut-off</span>
          <Input type="date" value={cutoffDate} onChange={e => { setCutoffDate(e.target.value); saveConfig({ cutoff_date: e.target.value }); }}
            className="h-8 w-36 text-xs" data-testid="pl-cutoff-date" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground font-medium">From</span>
          <Input type="month" value={startYm} onChange={e => { setStartYm(e.target.value); saveConfig({ start_ym: e.target.value }); }}
            className="h-8 w-32 text-xs" data-testid="pl-start-ym" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground font-medium">To</span>
          <Input type="month" value={endYm} onChange={e => { setEndYm(e.target.value); saveConfig({ end_ym: e.target.value }); }}
            className="h-8 w-32 text-xs" data-testid="pl-end-ym" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground font-medium">Scenario</span>
          <Select value={scenario} onValueChange={setScenario}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="pl-scenario-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SCENARIOS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setCompareMode(!compareMode)}
            className={cn("flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-colors",
              compareMode ? "bg-violet-100 border-violet-300 text-violet-700 dark:bg-violet-950 dark:border-violet-700 dark:text-violet-300" : "border-border text-muted-foreground hover:text-foreground"
            )} data-testid="pl-compare-toggle">
            <ArrowLeftRight className="w-3 h-3" />Compare
          </button>
          {compareMode && (
            <Select value={compareScenario} onValueChange={setCompareScenario}>
              <SelectTrigger className="w-32 h-8 text-xs border-violet-300" data-testid="pl-compare-scenario"><SelectValue /></SelectTrigger>
              <SelectContent>{SCENARIOS.filter(s => s !== scenario).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setPredictedVsActual(!predictedVsActual)}
            className={cn("flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-colors",
              predictedVsActual ? "bg-amber-100 border-amber-300 text-amber-700 dark:bg-amber-950 dark:border-amber-700 dark:text-amber-300" : "border-border text-muted-foreground hover:text-foreground"
            )} data-testid="pl-predicted-vs-actual-toggle">
            <Gauge className="w-3 h-3" />Predicted vs Actual
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportCSV} data-testid="pl-export-btn">
            <Download className="w-3.5 h-3.5 mr-1" />CSV
          </Button>
        </div>
      </div>

      {/* P/L GRID */}
      <Card className="border shadow-sm bg-card overflow-hidden" data-testid="pl-grid">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" style={{ minWidth: `${200 + buckets.length * 95}px` }}>
            <thead className="bg-slate-100 dark:bg-slate-900 sticky top-0 z-20">
              <tr>
                <th className="p-2 text-left font-semibold sticky left-0 bg-slate-100 dark:bg-slate-900 z-30 min-w-[200px] border-r border-border">Category</th>
                {buckets.map((b, i) => (
                  <th key={b} className={cn("p-2 text-right font-medium min-w-[95px] whitespace-nowrap",
                    i <= cutoffIdx ? "bg-slate-100 dark:bg-slate-900" : "bg-blue-100 dark:bg-blue-950",
                    i === cutoffIdx && "border-r-2 border-amber-500"
                  )}>
                    <span className="block">{bucketLabel(b, grouping)}</span>
                    {i === cutoffIdx && <span className="text-[9px] text-amber-600 font-normal block">cut-off</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* EXPENSES */}
              <tr className="bg-red-100 dark:bg-red-950">
                <td className="p-2 font-bold text-red-600 sticky left-0 bg-red-100 dark:bg-red-950 z-10 border-r border-border">
                  <div className="flex items-center justify-between">
                    <span>EXPENSES</span>
                    <button onClick={() => setAddCatModal('expense')} className="text-red-400 hover:text-red-600" title="Add forecast category" data-testid="add-expense-cat-btn">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
                {buckets.map((b, i) => <td key={b} className={cn("p-2", i === cutoffIdx && "border-r-2 border-amber-500")} />)}
              </tr>
              {expCats.map(cat => {
                const isExp = expanded.has(`exp_${cat.id}`);
                const hasSubs = cat.subs && cat.subs.length > 0;
                return (
                  <React.Fragment key={`exp_${cat.id}`}>
                    <tr className="border-t border-border hover:bg-muted/20 group" data-testid={`pl-expense-row-${cat.id}`}>
                      <td className="p-2 sticky left-0 bg-card z-10 border-r border-border">
                        <div className="flex items-center gap-1">
                          {hasSubs ? (
                            <button onClick={() => toggle(`exp_${cat.id}`)} className="shrink-0">
                              {isExp ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                            </button>
                          ) : <span className="w-3" />}
                          {cat.code && <span className="text-[9px] font-mono text-muted-foreground shrink-0">{cat.code}</span>}
                          <span className="font-medium truncate">{cat.name}</span>
                          {cat.isForecastOnly && (
                            <>
                              <Badge variant="outline" className="text-[8px] ml-1 shrink-0">Forecast</Badge>
                              <button onClick={() => deleteCustomCategory('expense', cat.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 ml-auto shrink-0">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      {buckets.map((b, i) => renderCell('expense', cat.id, b, i))}
                    </tr>
                    {/* Subcategory rows */}
                    {isExp && hasSubs && cat.subs.map(sub => (
                      <tr key={`sub_${cat.id}_${sub.name}`} className="border-t border-border/50 bg-muted/5" data-testid={`pl-expense-sub-${sub.name}`}>
                        <td className="p-1.5 pl-8 sticky left-0 bg-card z-10 border-r border-border">
                          <span className="text-[11px] text-muted-foreground truncate block">
                            {sub.code && <span className="font-mono text-[9px] mr-1 opacity-60">{sub.code}</span>}
                            {sub.name}
                          </span>
                        </td>
                        {buckets.map((b, i) => renderSubCell('expense', cat.id, sub.name, b, i))}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              {/* Expense totals */}
              <tr className="border-t-2 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950 font-bold">
                <td className="p-2 sticky left-0 bg-red-100 dark:bg-red-950 z-10 border-r border-border text-red-600">Total Expenses</td>
                {buckets.map((b, i) => (
                  <td key={b} className={cn("p-2 text-right font-mono text-red-600", i === cutoffIdx && "border-r-2 border-amber-500")} data-testid={`pl-expense-total-${b}`}>{fmtMoney(totals.et[i])}</td>
                ))}
              </tr>
              <tr className="bg-red-50 dark:bg-red-950 text-muted-foreground">
                <td className="p-2 sticky left-0 bg-red-50 dark:bg-red-950 z-10 border-r border-border text-xs italic">Cumulative Expenses</td>
                {buckets.map((b, i) => (
                  <td key={b} className={cn("p-2 text-right font-mono text-xs", i === cutoffIdx && "border-r-2 border-amber-500")}>{fmtMoney(totals.ceArr[i])}</td>
                ))}
              </tr>

              <tr><td className="p-1" colSpan={buckets.length + 1} /></tr>

              {/* INCOME */}
              <tr className="bg-emerald-100 dark:bg-emerald-950">
                <td className="p-2 font-bold text-emerald-600 sticky left-0 bg-emerald-100 dark:bg-emerald-950 z-10 border-r border-border">
                  <div className="flex items-center justify-between">
                    <span>INCOME</span>
                    <button onClick={() => setAddCatModal('income')} className="text-emerald-400 hover:text-emerald-600" title="Add forecast category" data-testid="add-income-cat-btn">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
                {buckets.map((b, i) => <td key={b} className={cn("p-2", i === cutoffIdx && "border-r-2 border-amber-500")} />)}
              </tr>
              {incCats.map(cat => {
                const isExp = expanded.has(`inc_${cat.id}`);
                const hasSubs = cat.subs && cat.subs.length > 0;
                return (
                  <React.Fragment key={`inc_${cat.id}`}>
                    <tr className="border-t border-border hover:bg-muted/20 group" data-testid={`pl-income-row-${cat.id}`}>
                      <td className="p-2 sticky left-0 bg-card z-10 border-r border-border">
                        <div className="flex items-center gap-1">
                          {hasSubs ? (
                            <button onClick={() => toggle(`inc_${cat.id}`)} className="shrink-0">
                              {isExp ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                            </button>
                          ) : <span className="w-3" />}
                          {cat.code && <span className="text-[9px] font-mono text-muted-foreground shrink-0">{cat.code}</span>}
                          <span className="font-medium truncate">{cat.name}</span>
                          {cat.isForecastOnly && (
                            <>
                              <Badge variant="outline" className="text-[8px] ml-1 shrink-0">Forecast</Badge>
                              <button onClick={() => deleteCustomCategory('income', cat.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 ml-auto shrink-0">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      {buckets.map((b, i) => renderCell('income', cat.id, b, i))}
                    </tr>
                    {isExp && hasSubs && cat.subs.map(sub => (
                      <tr key={`sub_${cat.id}_${sub.name}`} className="border-t border-border/50 bg-muted/5" data-testid={`pl-income-sub-${sub.name}`}>
                        <td className="p-1.5 pl-8 sticky left-0 bg-card z-10 border-r border-border">
                          <span className="text-[11px] text-muted-foreground truncate block">
                            {sub.code && <span className="font-mono text-[9px] mr-1 opacity-60">{sub.code}</span>}
                            {sub.name}
                          </span>
                        </td>
                        {buckets.map((b, i) => renderSubCell('income', cat.id, sub.name, b, i))}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              {/* Income totals */}
              <tr className="border-t-2 border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 font-bold">
                <td className="p-2 sticky left-0 bg-emerald-100 dark:bg-emerald-950 z-10 border-r border-border text-emerald-600">Total Income</td>
                {buckets.map((b, i) => (
                  <td key={b} className={cn("p-2 text-right font-mono text-emerald-600", i === cutoffIdx && "border-r-2 border-amber-500")} data-testid={`pl-income-total-${b}`}>{fmtMoney(totals.it[i])}</td>
                ))}
              </tr>
              <tr className="bg-emerald-50 dark:bg-emerald-950 text-muted-foreground">
                <td className="p-2 sticky left-0 bg-emerald-50 dark:bg-emerald-950 z-10 border-r border-border text-xs italic">Cumulative Income</td>
                {buckets.map((b, i) => (
                  <td key={b} className={cn("p-2 text-right font-mono text-xs", i === cutoffIdx && "border-r-2 border-amber-500")}>{fmtMoney(totals.ciArr[i])}</td>
                ))}
              </tr>

              <tr><td className="p-1" colSpan={buckets.length + 1} /></tr>

              {/* SUMMARY */}
              <tr className="bg-slate-200 dark:bg-slate-800 font-bold border-t-2 border-slate-400 dark:border-slate-600">
                <td className="p-2 sticky left-0 bg-slate-200 dark:bg-slate-800 z-10 border-r border-border">P/L (Net)</td>
                {buckets.map((b, i) => {
                  const delta = compareTotals ? compareTotals.pl[i] - totals.pl[i] : null;
                  return (
                    <td key={b} className={cn("p-2 text-right font-mono font-bold",
                      totals.pl[i] >= 0 ? "text-emerald-600" : "text-red-600",
                      i === cutoffIdx && "border-r-2 border-amber-500"
                    )} data-testid={`pl-net-${b}`}>
                      {fmtMoney(totals.pl[i])}
                      {delta !== null && Math.abs(delta) > 0.01 && i > cutoffIdx && (
                        <span className={cn("block text-[9px] mt-0.5 font-normal", delta >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {delta > 0 ? '+' : ''}{fmtMoney(delta)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
              <tr className="bg-slate-100 dark:bg-slate-800 font-semibold">
                <td className="p-2 sticky left-0 bg-slate-100 dark:bg-slate-800 z-10 border-r border-border">Running Balance</td>
                {buckets.map((b, i) => {
                  const delta = compareTotals ? compareTotals.rb[i] - totals.rb[i] : null;
                  return (
                    <td key={b} className={cn("p-2 text-right font-mono",
                      totals.rb[i] >= 0 ? "text-emerald-600" : "text-red-600",
                      i === cutoffIdx && "border-r-2 border-amber-500"
                    )} data-testid={`pl-balance-${b}`}>
                      {fmtMoney(totals.rb[i])}
                      {delta !== null && Math.abs(delta) > 0.01 && i > cutoffIdx && (
                        <span className={cn("block text-[9px] mt-0.5 font-normal", delta >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {delta > 0 ? '+' : ''}{fmtMoney(delta)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
              {/* Comparison summary row */}
              {compareMode && compareTotals && (
                <tr className="bg-violet-50 dark:bg-violet-950 border-t border-violet-200 dark:border-violet-800">
                  <td className="p-2 sticky left-0 bg-violet-50 dark:bg-violet-950 z-10 border-r border-border text-xs text-violet-600 font-medium">
                    {compareScenario} Balance
                  </td>
                  {buckets.map((b, i) => (
                    <td key={b} className={cn("p-2 text-right font-mono text-xs text-violet-600",
                      i === cutoffIdx && "border-r-2 border-amber-500"
                    )}>{fmtMoney(compareTotals.rb[i])}</td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* FORECAST DRIVERS */}
      <div className="space-y-3" data-testid="pl-drivers-section">
        <h3 className="text-base font-semibold" data-testid="expense-drivers-heading">Expense Forecast Drivers</h3>

        {/* ========== STAFFING SECTION (Special two-part layout) ========== */}
        <Card className="border shadow-sm bg-card overflow-hidden" data-testid="driver-section-staffing">
          <button
            className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/20 transition-colors text-left"
            onClick={() => setDriverTab(driverTab === 'staffing' ? null : 'staffing')}
            data-testid="driver-tab-staffing"
          >
            <Users className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-sm flex-1">Staffing</span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {staffingActuals.staff.length + drivers.filter(d => d.driver_type === 'staffing').length}
            </Badge>
            <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", driverTab === 'staffing' && "rotate-180")} />
          </button>

          {driverTab === 'staffing' && (
            <StaffingDriverSection
              staffingActuals={staffingActuals} drivers={drivers} buckets={buckets}
              cutoffIdx={cutoffIdx} grouping={grouping}
              computeDriverRow={computeDriverRow} bucketLabel={bucketLabel}
              setEditingDriver={setEditingDriver} setDriverForm={setDriverForm}
              setDriverModal={setDriverModal} deleteDriverFn={deleteDriverFn}
              saveDriverFn={saveDriverFn} workspaceId={workspaceId}
              orgMembers={orgMembers} expCats={expCats} catCodeMap={catCodeMap}
            />
          )}
        </Card>

        {/* ========== EXPENSE CATEGORY DRIVER SECTIONS ========== */}
        {EXPENSE_DRIVER_SECTIONS.map(section => {
          const sectionDrivers = drivers.filter(d => d.driver_type === section.key)
            .sort((a, b) => naturalCompare(a.sub_type || '', b.sub_type || ''));
          const isExpanded = driverTab === section.key;
          // Find actuals for this category by matching category names
          const sectionActuals = {};
          for (const matchKey of section.categoryMatch) {
            if (categoryItemActuals[matchKey]) {
              for (const [sub, items] of Object.entries(categoryItemActuals[matchKey])) {
                if (!sectionActuals[sub]) sectionActuals[sub] = [];
                sectionActuals[sub].push(...items);
              }
            }
          }
          const actualCount = Object.values(sectionActuals).reduce((s, items) => s + items.length, 0);
          return (
            <Card key={section.key} className="border shadow-sm bg-card overflow-hidden" data-testid={`driver-section-${section.key}`}>
              <button
                className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/20 transition-colors text-left"
                onClick={() => setDriverTab(isExpanded ? null : section.key)}
                data-testid={`driver-tab-${section.key}`}
              >
                <section.icon className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium text-sm flex-1">{section.label}</span>
                <Badge variant="secondary" className="text-[10px] px-1.5">{actualCount + sectionDrivers.length}</Badge>
                <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", isExpanded && "rotate-180")} />
              </button>
              {isExpanded && (
                <CategoryDriverSection
                  sectionKey={section.key}
                  categoryActuals={sectionActuals}
                  drivers={sectionDrivers}
                  buckets={buckets}
                  cutoffIdx={cutoffIdx}
                  grouping={grouping}
                  bucketLabel={(b) => bucketLabel(b, grouping)}
                  computeDriverRow={computeDriverRow}
                  catCodeMap={catCodeMap}
                  workspaceId={workspaceId}
                  vendors={refVendors}
                  orgMembers={orgMembers}
                  staffingActuals={staffingActuals}
                  onDriverSaved={fetchData}
                  onDriverDeleted={fetchData}
                />
              )}
            </Card>
          );
        })}

        {/* ========== INCOME FORECAST DRIVERS ========== */}
        <h3 className="text-base font-semibold mt-6" data-testid="income-drivers-heading">Income Forecast Drivers</h3>

        {/* B2B Income Section */}
        <Card className="border shadow-sm bg-card overflow-hidden" data-testid="driver-section-income-b2b">
          <button
            className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/20 transition-colors text-left"
            onClick={() => setDriverTab(driverTab === 'income_b2b' ? null : 'income_b2b')}
            data-testid="driver-tab-income-b2b"
          >
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            <span className="font-medium text-sm flex-1">B2B Revenue</span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {drivers.filter(d => d.driver_type === 'income_projection' && (d.market || '').toLowerCase().includes('b2b')).length}
            </Badge>
            <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", driverTab === 'income_b2b' && "rotate-180")} />
          </button>
          {driverTab === 'income_b2b' && (
            <div className="border-t">
              <div className="flex items-center justify-between px-4 py-2 bg-emerald-50/30 dark:bg-emerald-950/10">
                <p className="text-xs text-muted-foreground">B2B revenue streams: TopSchool Pilot, Enterprise, API</p>
                <Button size="sm" onClick={() => { setEditingDriver(null); setDriverForm({ market: 'B2B' }); setDriverModal(true); }} data-testid="add-b2b-driver-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Stream
                </Button>
              </div>
              <IncomeDriverGrid
                drivers={drivers.filter(d => d.driver_type === 'income_projection' && (d.market || '').toLowerCase().includes('b2b'))}
                buckets={buckets} cutoffIdx={cutoffIdx} grouping={grouping}
                bucketLabel={(b) => bucketLabel(b, grouping)} computeDriverRow={computeDriverRow}
                onEdit={d => { setEditingDriver(d); setDriverForm(d); setDriverModal(true); }}
                onDelete={deleteDriverFn}
              />
            </div>
          )}
        </Card>

        {/* B2C Income Section */}
        <Card className="border shadow-sm bg-card overflow-hidden" data-testid="driver-section-income-b2c">
          <button
            className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/20 transition-colors text-left"
            onClick={() => setDriverTab(driverTab === 'income_b2c' ? null : 'income_b2c')}
            data-testid="driver-tab-income-b2c"
          >
            <TrendingUp className="w-4 h-4 text-teal-500" />
            <span className="font-medium text-sm flex-1">B2C Revenue</span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {drivers.filter(d => d.driver_type === 'income_projection' && (d.market || '').toLowerCase().includes('b2c')).length}
            </Badge>
            <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", driverTab === 'income_b2c' && "rotate-180")} />
          </button>
          {driverTab === 'income_b2c' && (
            <div className="border-t">
              <div className="flex items-center justify-between px-4 py-2 bg-teal-50/30 dark:bg-teal-950/10">
                <p className="text-xs text-muted-foreground">B2C revenue streams: TopTeacher, TopParent</p>
                <Button size="sm" onClick={() => { setEditingDriver(null); setDriverForm({ market: 'B2C' }); setDriverModal(true); }} data-testid="add-b2c-driver-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Stream
                </Button>
              </div>
              <IncomeDriverGrid
                drivers={drivers.filter(d => d.driver_type === 'income_projection' && (d.market || '').toLowerCase().includes('b2c'))}
                buckets={buckets} cutoffIdx={cutoffIdx} grouping={grouping}
                bucketLabel={(b) => bucketLabel(b, grouping)} computeDriverRow={computeDriverRow}
                onEdit={d => { setEditingDriver(d); setDriverForm(d); setDriverModal(true); }}
                onDelete={deleteDriverFn}
              />
            </div>
          )}
        </Card>

        {/* ========== INVESTMENTS (linked to Fundraising/Cap Table) ========== */}
        <h3 className="text-base font-semibold mt-6" data-testid="investments-drivers-heading">Investments</h3>
        <Card className="border shadow-sm bg-card overflow-hidden" data-testid="driver-section-investment">
          <button
            className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/20 transition-colors text-left"
            onClick={() => setDriverTab(driverTab === 'investment' ? null : 'investment')}
            data-testid="driver-tab-investment"
          >
            <DollarSign className="w-4 h-4 text-violet-500" />
            <span className="font-medium text-sm flex-1">Investments (linked to Fundraising tab)</span>
            <Badge variant="secondary" className="text-[10px] px-1.5">{drivers.filter(d => d.driver_type === 'investment').length}</Badge>
            <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", driverTab === 'investment' && "rotate-180")} />
          </button>
          {driverTab === 'investment' && (
            <div className="border-t">
              <div className="flex items-center justify-between px-4 py-2 bg-violet-50/30 dark:bg-violet-950/10">
                <p className="text-xs text-muted-foreground">Expected funding rounds and investor commitments. Links to the Fundraising (Cap Table) tab.</p>
                <Button size="sm" onClick={() => { setEditingDriver(null); setDriverForm({}); setDriverModal(true); }} data-testid="add-investment-driver-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Investment
                </Button>
              </div>
              <InvestmentDriverGrid
                drivers={drivers.filter(d => d.driver_type === 'investment')}
                buckets={buckets} cutoffIdx={cutoffIdx} grouping={grouping}
                bucketLabel={(b) => bucketLabel(b, grouping)} computeDriverRow={computeDriverRow}
                onEdit={d => { setEditingDriver(d); setDriverForm(d); setDriverModal(true); }}
                onDelete={deleteDriverFn}
              />
            </div>
          )}
        </Card>

      </div>

      {/* DRIVER MODAL */}
      <Dialog open={driverModal} onOpenChange={o => { if (!o) { setDriverModal(false); setDriverForm({}); setEditingDriver(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDriver ? 'Edit' : 'Add'} {(driverTab || 'staffing').replace('_', ' ')} Driver</DialogTitle>
          </DialogHeader>
          <DriverForm type={driverTab} form={driverForm} onChange={setDriverForm}
            expCats={expCats} incCats={incCats} orgMembers={orgMembers} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDriverModal(false)}>Cancel</Button>
            <Button onClick={saveDriverFn} data-testid="save-driver-btn">{editingDriver ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD CATEGORY MODAL */}
      <Dialog open={!!addCatModal} onOpenChange={o => { if (!o) { setAddCatModal(null); setNewCatName(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Forecast {addCatModal === 'expense' ? 'Expense' : 'Income'} Category</DialogTitle>
          </DialogHeader>
          <Input placeholder="Category name" value={newCatName} onChange={e => setNewCatName(e.target.value)}
            className="h-9" data-testid="new-cat-name-input" onKeyDown={e => e.key === 'Enter' && addCustomCategory(addCatModal)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCatModal(null)}>Cancel</Button>
            <Button onClick={() => addCustomCategory(addCatModal)} data-testid="save-cat-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ==================== INCOME DRIVER GRID ====================
function IncomeDriverGrid({ drivers: incDrivers, buckets, cutoffIdx, grouping, bucketLabel, computeDriverRow, onEdit, onDelete }) {
  if (!incDrivers.length) return (
    <div className="text-center py-6 text-muted-foreground">
      <p className="text-sm">No revenue streams yet. Add one to see projections.</p>
    </div>
  );
  return (
    <div className="overflow-x-auto max-h-[400px]">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-emerald-50 dark:bg-emerald-950 sticky top-0 z-10">
          <tr>
            <th className="p-2 text-left font-semibold sticky left-0 bg-emerald-50 dark:bg-emerald-950 z-20 min-w-[180px] border-r border-border">Stream</th>
            <th className="p-2 text-left font-medium min-w-[80px]">Details</th>
            {buckets.map((b, i) => (
              <th key={b} className={cn("p-2 text-right font-medium min-w-[85px] whitespace-nowrap",
                i <= cutoffIdx ? "bg-emerald-50 dark:bg-emerald-950" : "bg-emerald-100 dark:bg-emerald-900",
                i === cutoffIdx && "border-r-2 border-amber-500"
              )}>{bucketLabel(b)}</th>
            ))}
            <th className="p-2 text-right font-semibold min-w-[90px] bg-emerald-50 dark:bg-emerald-950">Total</th>
            <th className="p-2 text-right w-16 bg-emerald-50 dark:bg-emerald-950">Actions</th>
          </tr>
        </thead>
        <tbody>
          {incDrivers.map((d, idx) => {
            const contributions = computeDriverRow(d, buckets);
            const total = contributions.reduce((s, v) => s + v, 0);
            return (
              <tr key={d.driver_id || idx} className="border-t border-border hover:bg-muted/20" data-testid={`income-driver-row-${idx}`}>
                <td className="p-2 sticky left-0 bg-card z-10 border-r border-border font-medium">
                  <span>{d.name || 'Unnamed'}</span>
                  {d.scenario && d.scenario !== 'base' && <Badge variant="outline" className="ml-1 text-[8px] px-1 py-0">{d.scenario}</Badge>}
                </td>
                <td className="p-2 text-muted-foreground whitespace-nowrap text-[10px]">
                  {d.starting_customers || 0} cust, {fmtMoney(d.price_per_customer || 0)}/mo
                </td>
                {contributions.map((val, i) => (
                  <td key={buckets[i]} className={cn("p-2 text-right font-mono",
                    val > 0 && "text-emerald-600", val === 0 && "text-muted-foreground/40",
                    i === cutoffIdx && "border-r-2 border-amber-500",
                    i > cutoffIdx && "bg-emerald-50/20 dark:bg-emerald-950/5"
                  )}>{val !== 0 ? fmtMoney(val) : '-'}</td>
                ))}
                <td className="p-2 text-right font-mono font-semibold text-emerald-700">{fmtMoney(total)}</td>
                <td className="p-2 text-right">
                  <div className="flex gap-0.5 justify-end">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onEdit(d)}><Settings2 className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => onDelete(d.driver_id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </td>
              </tr>
            );
          })}
          <tr className="border-t-2 border-border bg-emerald-100/40 dark:bg-emerald-900/20 font-bold">
            <td className="p-2 sticky left-0 bg-emerald-100 dark:bg-emerald-900 z-10 border-r border-border">Total</td>
            <td className="p-2"></td>
            {buckets.map((b, i) => {
              const colTotal = incDrivers.reduce((s, d) => s + (computeDriverRow(d, buckets)[i] || 0), 0);
              return <td key={b} className={cn("p-2 text-right font-mono text-emerald-700", i === cutoffIdx && "border-r-2 border-amber-500")}>{colTotal !== 0 ? fmtMoney(colTotal) : '-'}</td>;
            })}
            <td className="p-2 text-right font-mono text-emerald-700">{fmtMoney(incDrivers.reduce((s, d) => s + computeDriverRow(d, buckets).reduce((a, v) => a + v, 0), 0))}</td>
            <td className="p-2"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ==================== INVESTMENT DRIVER GRID ====================
function InvestmentDriverGrid({ drivers: invDrivers, buckets, cutoffIdx, grouping, bucketLabel, computeDriverRow, onEdit, onDelete }) {
  if (!invDrivers.length) return (
    <div className="text-center py-6 text-muted-foreground">
      <p className="text-sm">No investment drivers yet. Add one to see projections.</p>
    </div>
  );
  return (
    <div className="overflow-x-auto max-h-[400px]">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-violet-50 dark:bg-violet-950 sticky top-0 z-10">
          <tr>
            <th className="p-2 text-left font-semibold sticky left-0 bg-violet-50 dark:bg-violet-950 z-20 min-w-[180px] border-r border-border">Investor</th>
            <th className="p-2 text-left font-medium min-w-[80px]">Type</th>
            {buckets.map((b, i) => (
              <th key={b} className={cn("p-2 text-right font-medium min-w-[85px] whitespace-nowrap",
                i <= cutoffIdx ? "bg-violet-50 dark:bg-violet-950" : "bg-violet-100 dark:bg-violet-900",
                i === cutoffIdx && "border-r-2 border-amber-500"
              )}>{bucketLabel(b)}</th>
            ))}
            <th className="p-2 text-right font-semibold min-w-[90px] bg-violet-50 dark:bg-violet-950">Total</th>
            <th className="p-2 text-right w-16 bg-violet-50 dark:bg-violet-950">Actions</th>
          </tr>
        </thead>
        <tbody>
          {invDrivers.map((d, idx) => {
            const contributions = computeDriverRow(d, buckets);
            const total = contributions.reduce((s, v) => s + v, 0);
            return (
              <tr key={d.driver_id || idx} className="border-t border-border hover:bg-muted/20" data-testid={`investment-driver-row-${idx}`}>
                <td className="p-2 sticky left-0 bg-card z-10 border-r border-border font-medium">
                  <span>{d.investor_name || d.name || 'Unnamed'}</span>
                  {d.round_name && <div className="text-[10px] text-muted-foreground">{d.round_name}</div>}
                  {d.scenario && d.scenario !== 'base' && <Badge variant="outline" className="ml-1 text-[8px] px-1 py-0">{d.scenario}</Badge>}
                </td>
                <td className="p-2 text-muted-foreground whitespace-nowrap text-[10px]">
                  {d.investment_type || ''} &middot; {d.status || 'Planned'}
                </td>
                {contributions.map((val, i) => (
                  <td key={buckets[i]} className={cn("p-2 text-right font-mono",
                    val > 0 && "text-violet-600", val === 0 && "text-muted-foreground/40",
                    i === cutoffIdx && "border-r-2 border-amber-500",
                    i > cutoffIdx && "bg-violet-50/20 dark:bg-violet-950/5"
                  )}>{val !== 0 ? fmtMoney(val) : '-'}</td>
                ))}
                <td className="p-2 text-right font-mono font-semibold text-violet-700">{fmtMoney(total)}</td>
                <td className="p-2 text-right">
                  <div className="flex gap-0.5 justify-end">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onEdit(d)}><Settings2 className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => onDelete(d.driver_id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </td>
              </tr>
            );
          })}
          <tr className="border-t-2 border-border bg-violet-100/40 dark:bg-violet-900/20 font-bold">
            <td className="p-2 sticky left-0 bg-violet-100 dark:bg-violet-900 z-10 border-r border-border">Total</td>
            <td className="p-2"></td>
            {buckets.map((b, i) => {
              const colTotal = invDrivers.reduce((s, d) => s + (computeDriverRow(d, buckets)[i] || 0), 0);
              return <td key={b} className={cn("p-2 text-right font-mono text-violet-700", i === cutoffIdx && "border-r-2 border-amber-500")}>{colTotal !== 0 ? fmtMoney(colTotal) : '-'}</td>;
            })}
            <td className="p-2 text-right font-mono text-violet-700">{fmtMoney(invDrivers.reduce((s, d) => s + computeDriverRow(d, buckets).reduce((a, v) => a + v, 0), 0))}</td>
            <td className="p-2"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ==================== STAFFING DRIVER SECTION ====================
function StaffingDriverSection({ staffingActuals, drivers, buckets, cutoffIdx, grouping, computeDriverRow, bucketLabel, setEditingDriver, setDriverForm, setDriverModal, deleteDriverFn, saveDriverFn, workspaceId, orgMembers, expCats, catCodeMap }) {
  const [expandedGroups, setExpandedGroups] = useState(null); // null = all expanded initially
  const [expandedSubTypes, setExpandedSubTypes] = useState(null); // null = all expanded initially
  const [showArchived, setShowArchived] = useState(false);
  const [inlineEditing, setInlineEditing] = useState(null);
  const [inlineValue, setInlineValue] = useState('');
  const [addingSubType, setAddingSubType] = useState(false);
  const [newSubType, setNewSubType] = useState('');

  const toggleGroup = (name, allNames) => {
    setExpandedGroups(prev => {
      if (prev === null) {
        const n = new Set(allNames);
        n.delete(name);
        return n;
      }
      const n = new Set(prev);
      if (n.has(name)) n.delete(name); else n.add(name);
      return n;
    });
  };
  const toggleSubType = (name, allNames) => {
    setExpandedSubTypes(prev => {
      if (prev === null) {
        const n = new Set(allNames);
        n.delete(name);
        return n;
      }
      const n = new Set(prev);
      if (n.has(name)) n.delete(name); else n.add(name);
      return n;
    });
  };

  // Build sub-type-clustered current team with forecast projection (exclude archived)
  const currentTeamBySubType = useMemo(() => {
    const groups = {};
    for (const person of (staffingActuals.staff || [])) {
      if (person.is_archived) continue; // skip archived — they go to the archived section
      const subType = person.expense_sub_type || person.department || person.category || 'Uncategorised';
      if (!groups[subType]) groups[subType] = [];

      // Extend into forecast: for buckets after the last actual, project using annual_salary / 12
      const monthlySalary = person.annual_salary ? person.annual_salary / 12 : 0;
      const actualBucketKeys = Object.keys(person.buckets || {});
      const lastActualBucket = actualBucketKeys.length > 0 ? actualBucketKeys.sort().pop() : null;

      const extendedBuckets = { ...(person.buckets || {}) };
      if (monthlySalary > 0 && lastActualBucket) {
        for (const b of buckets) {
          if (b > lastActualBucket && !extendedBuckets[b]) {
            extendedBuckets[b] = monthlySalary;
          }
        }
      }

      groups[subType].push({ ...person, extendedBuckets });
    }
    return Object.entries(groups).sort((a, b) => naturalCompare(catCodeMap[a[0]] || a[0], catCodeMap[b[0]] || b[0]));
  }, [staffingActuals, buckets, catCodeMap]);

  // Archived staff from staffing actuals — these have real expense bucket data
  // Split into: off-boarded (truly archived) and active people with old roles
  const archivedStaffWithData = useMemo(() => {
    return (staffingActuals.staff || []).filter(p => p.is_archived).map(person => {
      const extendedBuckets = { ...(person.buckets || {}) };
      return { ...person, extendedBuckets };
    });
  }, [staffingActuals]);

  // Active people who had previous roles — their old-role expenses show in archive
  const activeWithOldRoles = useMemo(() => {
    const result = [];
    for (const person of (staffingActuals.staff || [])) {
      if (person.is_archived) continue;
      const rh = person.role_history || [];
      if (rh.length === 0) continue;
      // For each previous role, create a synthetic entry with just the buckets that fall in that role's period
      for (const hist of rh) {
        const roleBuckets = person.role_buckets?.[hist.role] || {};
        if (Object.keys(roleBuckets).length === 0) continue;
        result.push({
          ...person,
          name: person.name,
          job_title: hist.role,
          old_department: hist.department || person.department,
          expense_sub_type: hist.department || person.expense_sub_type,
          extendedBuckets: roleBuckets,
          is_old_role: true,
          role_period: `${hist.start_date || '?'} - ${hist.end_date || '?'}`,
        });
      }
    }
    return result;
  }, [staffingActuals]);

  // Group both archived sections by sub-type/team
  const offBoardedByTeam = useMemo(() => {
    const groups = {};
    for (const p of archivedStaffWithData) {
      const team = p.expense_sub_type || p.department || 'Uncategorised';
      if (!groups[team]) groups[team] = [];
      groups[team].push(p);
    }
    return Object.entries(groups).sort((a, b) => naturalCompare(catCodeMap[a[0]] || a[0], catCodeMap[b[0]] || b[0]));
  }, [archivedStaffWithData, catCodeMap]);

  const oldRolesByTeam = useMemo(() => {
    const groups = {};
    for (const p of activeWithOldRoles) {
      const team = p.old_department || p.expense_sub_type || 'Uncategorised';
      if (!groups[team]) groups[team] = [];
      groups[team].push(p);
    }
    return Object.entries(groups).sort((a, b) => naturalCompare(catCodeMap[a[0]] || a[0], catCodeMap[b[0]] || b[0]));
  }, [activeWithOldRoles, catCodeMap]);

  // Combined archived totals for the Total row
  const allArchivedForTotals = useMemo(() => {
    return [...archivedStaffWithData, ...activeWithOldRoles];
  }, [archivedStaffWithData, activeWithOldRoles]);

  // Planned hires by sub-type
  const staffDrivers = useMemo(() => drivers.filter(d => d.driver_type === 'staffing'), [drivers]);
  const subTypeGroups = useMemo(() => {
    const groups = {};
    for (const d of staffDrivers) {
      const subType = d.sub_type || d.team || 'Unassigned';
      if (!groups[subType]) groups[subType] = [];
      groups[subType].push(d);
    }
    return Object.entries(groups).sort((a, b) => naturalCompare(catCodeMap[a[0]] || a[0], catCodeMap[b[0]] || b[0]));
  }, [staffDrivers, catCodeMap]);

  const allCurrentGroupNames = useMemo(() => currentTeamBySubType.map(([name]) => name), [currentTeamBySubType]);
  const allSubTypeNames = useMemo(() => subTypeGroups.map(([name]) => name), [subTypeGroups]);
  const isGroupExpanded = (name) => expandedGroups === null || expandedGroups.has(name);
  const isSubTypeExpanded = (name) => expandedSubTypes === null || expandedSubTypes.has(name);
  const allGroupsExpanded = expandedGroups === null || (allCurrentGroupNames.length > 0 && allCurrentGroupNames.every(n => expandedGroups.has(n)));
  const toggleAllGroups = () => {
    if (allGroupsExpanded) setExpandedGroups(new Set());
    else setExpandedGroups(null);
  };

  const startInlineEdit = (driverId, bucketIdx, currentVal) => {
    setInlineEditing({ driverId, bucketIdx });
    setInlineValue(String(currentVal || ''));
  };
  const saveInlineEdit = async () => {
    if (!inlineEditing) return;
    const driver = staffDrivers.find(d => d.driver_id === inlineEditing.driverId);
    if (!driver) return;
    const newMonthly = parseFloat(inlineValue) || 0;
    try {
      await fetch(`${API}/finance/pl/${workspaceId}/drivers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...driver, salary: newMonthly * 12 }),
      });
      toast.success('Updated');
    } catch { toast.error('Failed'); }
    setInlineEditing(null); setInlineValue('');
  };
  const cancelInlineEdit = () => { setInlineEditing(null); setInlineValue(''); };

  const addHireToSubType = (subType) => {
    setEditingDriver(null);
    setDriverForm({ sub_type: subType, team: subType });
    setDriverModal(true);
  };

  const stickyLeftCls = "sticky left-0 z-10";

  return (
    <div className="border-t">
      {/* ---- CURRENT TEAM clustered by sub-type ---- */}
      <div className="px-4 py-2 bg-emerald-50/50 dark:bg-emerald-950/20 border-b flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Current Team</p>
          <p className="text-[10px] text-muted-foreground">Actuals from expense ledger extended into forecast from salary settings. Clustered by sub-type.</p>
        </div>
        {currentTeamBySubType.length > 1 && (
          <button onClick={toggleAllGroups} className="text-[10px] font-medium text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 px-2 py-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors" data-testid="toggle-all-groups-btn">
            {allGroupsExpanded ? 'Collapse All' : 'Expand All'}
          </button>
        )}
      </div>
      {currentTeamBySubType.length > 0 ? (
        <div className="overflow-auto max-h-[500px] relative">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-emerald-50 dark:bg-emerald-950 z-20">
              <tr>
                <th className={cn("p-2 text-left font-semibold min-w-[220px] border-r border-border bg-emerald-50 dark:bg-emerald-950 sticky top-0 left-0 z-40")}>Name / Role</th>
                {buckets.map((b, i) => (
                  <th key={b} className={cn("p-2 text-right font-medium min-w-[85px] whitespace-nowrap sticky top-0 z-20",
                    i <= cutoffIdx ? "bg-emerald-50 dark:bg-emerald-950" : "bg-emerald-100 dark:bg-emerald-900",
                    i === cutoffIdx && "border-r-2 border-amber-500"
                  )}>{bucketLabel(b, grouping)}</th>
                ))}
                <th className="p-2 text-right font-semibold min-w-[90px] bg-emerald-50 dark:bg-emerald-950 sticky top-0 z-20">Total</th>
              </tr>
            </thead>
            <tbody>
              {currentTeamBySubType.map(([subType, people]) => {
                const isExpanded = isGroupExpanded(subType);
                const groupTotals = buckets.map(b => people.reduce((s, p) => s + (p.extendedBuckets[b] || 0), 0));
                const groupGrandTotal = groupTotals.reduce((a, v) => a + v, 0);
                return (
                  <React.Fragment key={subType}>
                    <tr className="bg-emerald-50 dark:bg-emerald-900 border-t cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-800" onClick={() => toggleGroup(subType, allCurrentGroupNames)} data-testid={`current-group-${subType}`}>
                      <td className={cn("p-1.5 pl-2 border-r border-border bg-emerald-50 dark:bg-emerald-900", stickyLeftCls, "z-20")}>
                        <div className="flex items-center gap-1">
                          <ChevronDown className={cn("w-3 h-3 transition-transform", !isExpanded && "-rotate-90")} />
                          <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                            {catCodeMap[subType] && <span className="font-mono text-[9px] mr-1 opacity-70">{catCodeMap[subType]}</span>}
                            {subType} ({people.length})
                          </span>
                        </div>
                      </td>
                      {groupTotals.map((val, i) => (
                        <td key={buckets[i]} className={cn("p-1.5 text-right font-mono text-[10px] font-semibold text-emerald-700 dark:text-emerald-400",
                          i === cutoffIdx && "border-r-2 border-amber-500",
                          i <= cutoffIdx ? "bg-emerald-50 dark:bg-emerald-900" : "bg-emerald-100 dark:bg-emerald-800"
                        )}>{val > 0 ? fmtMoney(val) : '-'}</td>
                      ))}
                      <td className="p-1.5 text-right font-mono text-[10px] font-bold text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900">{fmtMoney(groupGrandTotal)}</td>
                    </tr>
                    {isExpanded && people.map((person, pidx) => {
                      const rowTotal = buckets.reduce((s, b) => s + (person.extendedBuckets[b] || 0), 0);
                      const hasHistory = person.role_history?.length > 0;
                      return (
                        <tr key={person.person_key || pidx} className="border-t border-border/50 hover:bg-muted/10" data-testid={`actual-staff-row-${pidx}`}>
                          <td className={cn("p-2 pl-6 border-r border-border bg-card", stickyLeftCls, "z-20")}>
                            <div className="font-medium text-foreground">{person.name || person.expense_item || 'Unknown'}</div>
                            {person.job_title && <div className="text-[10px] text-muted-foreground">{person.job_title}</div>}
                            {hasHistory && (
                              <details className="mt-0.5">
                                <summary className="text-[9px] text-muted-foreground/60 cursor-pointer hover:text-muted-foreground">{person.role_history.length} previous role{person.role_history.length > 1 ? 's' : ''}</summary>
                                <div className="mt-0.5 space-y-0.5 pl-2 border-l border-muted">
                                  {[...person.role_history].reverse().map((rh, ri) => (
                                    <div key={ri} className="text-[9px] text-muted-foreground/60">
                                      {rh.role}{rh.department ? ` (${rh.department})` : ''}{rh.end_date ? ` — until ${rh.end_date}` : ''}
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                            {person.annual_salary > 0 && <div className="text-[10px] text-emerald-600 dark:text-emerald-400">{fmtMoney(person.annual_salary)}/yr</div>}
                          </td>
                          {buckets.map((b, i) => {
                            const val = person.extendedBuckets[b] || 0;
                            const isActual = person.buckets && person.buckets[b] !== undefined;
                            const isProjection = val > 0 && !isActual;
                            return (
                              <td key={b} className={cn("p-2 text-right font-mono",
                                val > 0 ? (isProjection ? "text-emerald-500/70 italic" : "text-foreground") : "text-muted-foreground/30",
                                i === cutoffIdx && "border-r-2 border-amber-500",
                                i > cutoffIdx && "bg-emerald-50/10 dark:bg-emerald-950/5"
                              )}>{val > 0 ? fmtMoney(val) : '-'}</td>
                            );
                          })}
                          <td className="p-2 text-right font-mono font-semibold">{fmtMoney(rowTotal)}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {/* Total Current Team + Archived — STICKY + OPAQUE */}
              <tr className="border-t-2 border-border font-bold sticky bottom-0 z-20" data-testid="current-team-total-row">
                <td className={cn("p-2.5 border-r border-border bg-emerald-100 dark:bg-emerald-950 text-emerald-900 dark:text-emerald-200", stickyLeftCls, "z-30")}>Total Current Team</td>
                {buckets.map((b, i) => {
                  const currentTotal = currentTeamBySubType.flatMap(([, ppl]) => ppl).reduce((s, p) => s + (p.extendedBuckets[b] || 0), 0);
                  const archivedTotal = allArchivedForTotals.reduce((s, p) => s + (p.extendedBuckets[b] || 0), 0);
                  const colTotal = currentTotal + archivedTotal;
                  return (
                    <td key={b} className={cn("p-2.5 text-right font-mono bg-emerald-100 dark:bg-emerald-950 text-emerald-900 dark:text-emerald-200",
                      i === cutoffIdx && "border-r-2 border-amber-500"
                    )}>{colTotal > 0 ? fmtMoney(colTotal) : '-'}</td>
                  );
                })}
                <td className="p-2.5 text-right font-mono bg-emerald-100 dark:bg-emerald-950 text-emerald-900 dark:text-emerald-200">
                  {fmtMoney(
                    currentTeamBySubType.flatMap(([, ppl]) => ppl).reduce((s, p) => s + buckets.reduce((a, b) => a + (p.extendedBuckets[b] || 0), 0), 0) +
                    allArchivedForTotals.reduce((s, p) => s + buckets.reduce((a, b) => a + (p.extendedBuckets[b] || 0), 0), 0)
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-4 text-muted-foreground border-b">
          <p className="text-xs">No payroll expenses found. Add expenses with category &quot;Payroll &amp; Benefits&quot; to see current team here.</p>
        </div>
      )}

      {/* ---- ARCHIVED SECTIONS ---- */}
      {(offBoardedByTeam.length > 0 || oldRolesByTeam.length > 0) && (
        <div className="border-b">
          <button className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-900 flex items-center gap-2 hover:bg-slate-200 dark:hover:bg-slate-800 text-left" onClick={() => setShowArchived(!showArchived)} data-testid="archived-staff-toggle">
            <ChevronDown className={cn("w-3.5 h-3.5 transition-transform text-muted-foreground", !showArchived && "-rotate-90")} />
            <Archive className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground">Archived ({archivedStaffWithData.length + activeWithOldRoles.length})</span>
          </button>
          {showArchived && (
            <div className="space-y-0">
              {/* Off Boarded People */}
              {offBoardedByTeam.length > 0 && (
                <div>
                  <div className="px-4 py-1.5 bg-slate-50 dark:bg-slate-950 border-b">
                    <p className="text-[10px] font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider">Off Boarded People</p>
                  </div>
                  <div className="overflow-auto max-h-[250px]">
                    <table className="w-full text-xs border-collapse">
                      <thead className="bg-slate-100 dark:bg-slate-900 z-20">
                        <tr>
                          <th className="p-2 text-left font-semibold min-w-[220px] border-r border-border bg-slate-100 dark:bg-slate-900 sticky top-0 left-0 z-40">Name / Role</th>
                          {buckets.map((b, i) => (
                            <th key={b} className={cn("p-2 text-right font-medium min-w-[85px] whitespace-nowrap bg-slate-100 dark:bg-slate-900 sticky top-0 z-20",
                              i === cutoffIdx && "border-r-2 border-amber-500"
                            )}>{bucketLabel(b, grouping)}</th>
                          ))}
                          <th className="p-2 text-right font-semibold min-w-[90px] bg-slate-100 dark:bg-slate-900 sticky top-0 z-20">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {offBoardedByTeam.map(([team, people]) => (
                          <React.Fragment key={team}>
                            <tr className="bg-slate-50 dark:bg-slate-900/50 border-t">
                              <td className={cn("p-1.5 pl-2 border-r border-border bg-slate-50 dark:bg-slate-900/50 sticky left-0 z-10")} colSpan={1}>
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                  {catCodeMap[team] && <span className="font-mono text-[9px] mr-1 opacity-70">{catCodeMap[team]}</span>}
                                  {team} ({people.length})
                                </span>
                              </td>
                              {buckets.map((b, i) => {
                                const teamTotal = people.reduce((s, p) => s + (p.extendedBuckets[b] || 0), 0);
                                return <td key={b} className={cn("p-1.5 text-right font-mono text-[10px] font-semibold text-slate-500", i === cutoffIdx && "border-r-2 border-amber-500")}>{teamTotal > 0 ? fmtMoney(teamTotal) : '-'}</td>;
                              })}
                              <td className="p-1.5 text-right font-mono text-[10px] font-bold text-slate-500">{fmtMoney(people.reduce((s, p) => s + buckets.reduce((a, b) => a + (p.extendedBuckets[b] || 0), 0), 0))}</td>
                            </tr>
                            {people.map((person, idx) => {
                              const rowTotal = buckets.reduce((s, b) => s + (person.extendedBuckets[b] || 0), 0);
                              return (
                                <tr key={person.person_key || idx} className="border-t border-border/50 hover:bg-muted/10 opacity-60" data-testid={`offboarded-row-${idx}`}>
                                  <td className={cn("p-2 pl-6 border-r border-border bg-card sticky left-0 z-10")}>
                                    <div className="font-medium">{person.name || 'Unknown'}</div>
                                    {person.job_title && <div className="text-[10px] text-muted-foreground">{person.job_title}</div>}
                                  </td>
                                  {buckets.map((b, i) => {
                                    const val = person.extendedBuckets[b] || 0;
                                    return <td key={b} className={cn("p-2 text-right font-mono text-muted-foreground", val === 0 && "text-muted-foreground/30", i === cutoffIdx && "border-r-2 border-amber-500")}>{val > 0 ? fmtMoney(val) : '-'}</td>;
                                  })}
                                  <td className="p-2 text-right font-mono font-semibold text-muted-foreground">{fmtMoney(rowTotal)}</td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Active People (now in different roles) */}
              {oldRolesByTeam.length > 0 && (
                <div>
                  <div className="px-4 py-1.5 bg-slate-50 dark:bg-slate-950 border-b border-t">
                    <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Active People (now in different roles)</p>
                  </div>
                  <div className="overflow-auto max-h-[250px]">
                    <table className="w-full text-xs border-collapse">
                      <thead className="bg-slate-100 dark:bg-slate-900 z-20">
                        <tr>
                          <th className="p-2 text-left font-semibold min-w-[220px] border-r border-border bg-slate-100 dark:bg-slate-900 sticky top-0 left-0 z-40">Name / Previous Role</th>
                          {buckets.map((b, i) => (
                            <th key={b} className={cn("p-2 text-right font-medium min-w-[85px] whitespace-nowrap bg-slate-100 dark:bg-slate-900 sticky top-0 z-20",
                              i === cutoffIdx && "border-r-2 border-amber-500"
                            )}>{bucketLabel(b, grouping)}</th>
                          ))}
                          <th className="p-2 text-right font-semibold min-w-[90px] bg-slate-100 dark:bg-slate-900 sticky top-0 z-20">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {oldRolesByTeam.map(([team, people]) => (
                          <React.Fragment key={team}>
                            <tr className="bg-amber-50/40 dark:bg-amber-900/10 border-t">
                              <td className={cn("p-1.5 pl-2 border-r border-border bg-amber-50/40 dark:bg-amber-900/10 sticky left-0 z-10")} colSpan={1}>
                                <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                                  {catCodeMap[team] && <span className="font-mono text-[9px] mr-1 opacity-70">{catCodeMap[team]}</span>}
                                  {team} ({people.length})
                                </span>
                              </td>
                              {buckets.map((b, i) => {
                                const teamTotal = people.reduce((s, p) => s + (p.extendedBuckets[b] || 0), 0);
                                return <td key={b} className={cn("p-1.5 text-right font-mono text-[10px] font-semibold text-amber-600", i === cutoffIdx && "border-r-2 border-amber-500")}>{teamTotal > 0 ? fmtMoney(teamTotal) : '-'}</td>;
                              })}
                              <td className="p-1.5 text-right font-mono text-[10px] font-bold text-amber-600">{fmtMoney(people.reduce((s, p) => s + buckets.reduce((a, b) => a + (p.extendedBuckets[b] || 0), 0), 0))}</td>
                            </tr>
                            {people.map((person, idx) => {
                              const rowTotal = buckets.reduce((s, b) => s + (person.extendedBuckets[b] || 0), 0);
                              return (
                                <tr key={`${person.person_key}-${person.job_title}-${idx}`} className="border-t border-border/50 hover:bg-muted/10 opacity-70" data-testid={`old-role-row-${idx}`}>
                                  <td className={cn("p-2 pl-6 border-r border-border bg-card sticky left-0 z-10")}>
                                    <div className="font-medium">{person.name || 'Unknown'}</div>
                                    <div className="text-[10px] text-amber-600">{person.job_title}</div>
                                    <div className="text-[9px] text-muted-foreground/50">{person.role_period}</div>
                                  </td>
                                  {buckets.map((b, i) => {
                                    const val = person.extendedBuckets[b] || 0;
                                    return <td key={b} className={cn("p-2 text-right font-mono text-muted-foreground", val === 0 && "text-muted-foreground/30", i === cutoffIdx && "border-r-2 border-amber-500")}>{val > 0 ? fmtMoney(val) : '-'}</td>;
                                  })}
                                  <td className="p-2 text-right font-mono font-semibold text-muted-foreground">{fmtMoney(rowTotal)}</td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---- PLANNED HIRES (clustered by sub-type) ---- */}
      <div className="px-4 py-2 bg-blue-50/50 dark:bg-blue-950/20 border-b flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-blue-700 dark:text-blue-400">Planned Hires - Forecast</p>
          <p className="text-[10px] text-muted-foreground">Clustered by sub-type. Click cells to override salary modelling.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setAddingSubType(true)} data-testid="add-sub-type-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Add Sub-Type
          </Button>
          <Button size="sm" onClick={() => { setEditingDriver(null); setDriverForm({}); setDriverModal(true); }} data-testid="add-staff-driver-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Add Hire
          </Button>
        </div>
      </div>

      {addingSubType && (
        <div className="px-4 py-2 bg-blue-50/30 border-b flex items-center gap-2">
          <Input value={newSubType} onChange={e => setNewSubType(e.target.value)} placeholder="New sub-type name..." className="h-7 text-xs w-60" data-testid="new-sub-type-input" />
          <Button size="sm" className="h-7 text-xs" onClick={() => { if (newSubType.trim()) { addHireToSubType(newSubType.trim()); setAddingSubType(false); setNewSubType(''); } }} data-testid="create-sub-type-btn">Create &amp; Add Hire</Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setAddingSubType(false); setNewSubType(''); }}>Cancel</Button>
        </div>
      )}

      {subTypeGroups.length > 0 ? (
        <div className="overflow-auto max-h-[400px] relative">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-blue-50 dark:bg-blue-950 z-20">
              <tr>
                <th className={cn("p-2 text-left font-semibold min-w-[220px] border-r border-border bg-blue-50 dark:bg-blue-950 sticky top-0 left-0 z-40")}>Name / Role</th>
                {buckets.map((b, i) => (
                  <th key={b} className={cn("p-2 text-right font-medium min-w-[85px] whitespace-nowrap sticky top-0 z-20",
                    i <= cutoffIdx ? "bg-blue-50 dark:bg-blue-950" : "bg-blue-100 dark:bg-blue-900",
                    i === cutoffIdx && "border-r-2 border-amber-500"
                  )}>{bucketLabel(b, grouping)}</th>
                ))}
                <th className="p-2 text-right font-semibold min-w-[90px] bg-blue-50 dark:bg-blue-950 sticky top-0 z-20">Total</th>
              </tr>
            </thead>
            <tbody>
              {subTypeGroups.map(([subType, hires]) => {
                const isExpanded = isSubTypeExpanded(subType);
                const groupContributions = buckets.map((b, i) => hires.reduce((s, d) => s + (computeDriverRow(d, buckets)[i] || 0), 0));
                return (
                  <React.Fragment key={subType}>
                    <tr className="bg-blue-50 dark:bg-blue-900 border-t cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800" onClick={() => toggleSubType(subType, allSubTypeNames)} data-testid={`subtype-group-${subType}`}>
                      <td className={cn("p-1.5 pl-2 border-r border-border bg-blue-50 dark:bg-blue-900", stickyLeftCls, "z-20")}>
                        <div className="flex items-center gap-1.5">
                          <ChevronDown className={cn("w-3 h-3 transition-transform", !isExpanded && "-rotate-90")} />
                          <span className="text-[10px] font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wider">
                            {catCodeMap[subType] && <span className="font-mono text-[9px] mr-1 opacity-70">{catCodeMap[subType]}</span>}
                            {subType} ({hires.length})
                          </span>
                          <Button variant="ghost" size="icon" className="h-5 w-5 ml-1" title="Add hire" onClick={e => { e.stopPropagation(); addHireToSubType(subType); }}>
                            <Plus className="w-2.5 h-2.5" />
                          </Button>
                        </div>
                      </td>
                      {groupContributions.map((val, i) => (
                        <td key={buckets[i]} className={cn("p-1.5 text-right font-mono text-[10px] font-semibold text-blue-600",
                          i === cutoffIdx && "border-r-2 border-amber-500"
                        )}>{val !== 0 ? fmtMoney(val) : '-'}</td>
                      ))}
                      <td className="p-1.5 text-right font-mono text-[10px] font-bold text-blue-700">{fmtMoney(groupContributions.reduce((a, v) => a + v, 0))}</td>
                    </tr>
                    {isExpanded && hires.map((d, idx) => {
                      const contributions = computeDriverRow(d, buckets);
                      const total = contributions.reduce((s, v) => s + v, 0);
                      return (
                        <tr key={d.driver_id || idx} className="border-t border-border/50 hover:bg-muted/10 group" data-testid={`staff-driver-row-${d.driver_id}`}>
                          <td className={cn("p-2 pl-6 border-r border-border bg-card", stickyLeftCls, "z-20")}>
                            <div className="flex items-center gap-1">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium">{d.role || d.name || 'Unnamed'}</div>
                                {d.job_title && <div className="text-[10px] text-muted-foreground">{d.job_title}</div>}
                                {d.scenario && d.scenario !== 'base' && (
                                  <Badge variant="outline" className="text-[8px] px-1 py-0 mt-0.5">{d.scenario}</Badge>
                                )}
                              </div>
                              <div className="flex gap-0 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900 text-muted-foreground hover:text-foreground" onClick={() => { setEditingDriver(d); setDriverForm(d); setDriverModal(true); }} title="Edit" data-testid={`edit-hire-${d.driver_id}`}>
                                  <Pencil className="w-3 h-3" />
                                </button>
                                <button className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-500" onClick={() => deleteDriverFn(d.driver_id)} title="Delete" data-testid={`delete-hire-${d.driver_id}`}>
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          </td>
                          {contributions.map((val, i) => {
                            const isEditing = inlineEditing?.driverId === d.driver_id && inlineEditing?.bucketIdx === i;
                            return (
                              <td key={buckets[i]} className={cn("p-0 text-right",
                                i === cutoffIdx && "border-r-2 border-amber-500",
                                i > cutoffIdx && "bg-blue-50/10 dark:bg-blue-950/5"
                              )}>
                                {isEditing ? (
                                  <input className="w-full h-full p-2 text-right text-xs font-mono bg-blue-100 dark:bg-blue-900 border-0 outline-none" autoFocus
                                    value={inlineValue} onChange={e => setInlineValue(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') cancelInlineEdit(); }}
                                    onBlur={cancelInlineEdit} />
                                ) : (
                                  <button className={cn("w-full p-2 text-right font-mono hover:bg-blue-100/50 dark:hover:bg-blue-900/20 cursor-pointer",
                                    val > 0 && "text-red-500", val === 0 && "text-muted-foreground/30"
                                  )} onClick={() => startInlineEdit(d.driver_id, i, val)} title="Click to override">
                                    {val !== 0 ? fmtMoney(val) : '-'}
                                  </button>
                                )}
                              </td>
                            );
                          })}
                          <td className="p-2 text-right font-mono font-semibold">{fmtMoney(total)}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {/* Total Planned Hires — STICKY + OPAQUE */}
              <tr className="border-t-2 border-border font-bold sticky bottom-0 z-20" data-testid="planned-hires-total-row">
                <td className={cn("p-2.5 border-r border-border bg-blue-100 dark:bg-blue-950 text-blue-900 dark:text-blue-200", stickyLeftCls, "z-30")}>Total Planned Hires</td>
                {buckets.map((b, i) => {
                  const colTotal = staffDrivers.reduce((s, d) => s + (computeDriverRow(d, buckets)[i] || 0), 0);
                  return (
                    <td key={b} className={cn("p-2.5 text-right font-mono bg-blue-100 dark:bg-blue-950 text-blue-900 dark:text-blue-200",
                      i === cutoffIdx && "border-r-2 border-amber-500"
                    )}>{colTotal !== 0 ? fmtMoney(colTotal) : '-'}</td>
                  );
                })}
                <td className="p-2.5 text-right font-mono bg-blue-100 dark:bg-blue-950 text-blue-900 dark:text-blue-200">
                  {fmtMoney(staffDrivers.reduce((s, d) => s + computeDriverRow(d, buckets).reduce((a, v) => a + v, 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-4 text-muted-foreground">
          <p className="text-xs">No planned hires yet. Add future staff to see forecast projections.</p>
        </div>
      )}
    </div>
  );
}


// ==================== BURN RATE CARD ====================
function BurnCard({ label, value, color, sub }) {
  return (
    <Card className="border shadow-sm bg-card" data-testid={`burn-card-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <CardContent className="p-3">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{label}</p>
        <p className={cn("text-lg font-bold font-mono mt-0.5", color)}>{value}</p>
        {sub && <p className={cn("text-[10px] mt-0.5", color)}>{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ==================== DRIVER TABLE ====================
function DriverTable({ drivers, type, onEdit, onDelete }) {
  if (!drivers.length) return (
    <div className="text-center py-8 text-muted-foreground">
      <p className="text-sm">No {type.replace('_', ' ')} drivers yet.</p>
    </div>
  );

  const cols = {
    staffing: ['role', 'team', 'base_salary', 'benefits_pct', 'start_date', 'end_date', 'currency', 'scenario'],
    tools: ['tool_name', 'tool_category', 'monthly_cost', 'growth_pct', 'start_date', 'end_date', 'currency', 'scenario'],
    investment: ['investor_name', 'investment_type', 'round_name', 'expected_close_date', 'expected_amount', 'status', 'currency', 'scenario'],
    income_projection: ['name', 'market', 'starting_customers', 'monthly_new_customers', 'churn_pct', 'price_per_customer', 'sales_lag', 'scenario'],
  }[type] || [];

  const labels = {
    role:'Role', team:'Team', base_salary:'Annual Salary', benefits_pct:'Benefits %',
    start_date:'Start', end_date:'End', currency:'Ccy', scenario:'Scenario',
    tool_name:'Tool', tool_category:'Category', monthly_cost:'Monthly Cost', growth_pct:'Growth %',
    investor_name:'Investor', investment_type:'Type', round_name:'Round',
    expected_close_date:'Close Date', expected_amount:'Amount', status:'Status',
    name:'Product', market:'Market', starting_customers:'Start Cust.',
    monthly_new_customers:'New/Mo', churn_pct:'Churn %', price_per_customer:'Price/Cust', sales_lag:'Lag',
  };
  const moneyF = ['base_salary', 'monthly_cost', 'expected_amount', 'price_per_customer'];

  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-xs">
        <thead className="bg-muted/50">
          <tr>
            {cols.map(c => <th key={c} className="p-2 text-left font-medium whitespace-nowrap">{labels[c] || c}</th>)}
            <th className="p-2 text-right font-medium w-16">Actions</th>
          </tr>
        </thead>
        <tbody>
          {drivers.map((d, idx) => (
            <tr key={d.driver_id || idx} className="border-t border-border hover:bg-muted/20" data-testid={`driver-row-${idx}`}>
              {cols.map(c => <td key={c} className="p-2 whitespace-nowrap">{moneyF.includes(c) ? fmtMoneyFull(d[c]) : (d[c] ?? '-')}</td>)}
              <td className="p-2 text-right">
                <div className="flex gap-0.5 justify-end">
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onEdit(d)}><Settings2 className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => onDelete(d.driver_id)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ==================== DRIVER FORM ====================
function DriverForm({ type, form, onChange, expCats, incCats, orgMembers }) {
  const set = (k, v) => onChange(p => ({ ...p, [k]: v }));
  const expOptions = expCats.map(c => ({ value: c.id, label: c.code ? `${c.code} ${c.name}` : c.name }));
  const incOptions = incCats.map(c => ({ value: c.id, label: c.code ? `${c.code} ${c.name}` : c.name }));

  if (type === 'staffing') return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <label className="text-xs text-muted-foreground block mb-1">Link to Org Member (optional)</label>
        <Select value={form.linked_person_id || '_none'} onValueChange={v => {
          if (v === '_none') { set('linked_person_id', ''); return; }
          const member = (orgMembers || []).find(m => (m.person_id || m.user_id) === v);
          if (member) {
            onChange(p => ({ ...p, linked_person_id: v, role: p.role || member.name || '', job_title: member.role !== 'Workspace' ? member.role : '' }));
          } else { set('linked_person_id', v); }
        }}>
          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select person..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">— None —</SelectItem>
            {(orgMembers || []).map(m => (
              <SelectItem key={m.person_id || m.user_id} value={m.person_id || m.user_id}>
                {m.name || m.email}{m.has_authenticated ? ' ✓' : m.account_status === 'person_only' ? ' (person)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div><label className="text-xs text-muted-foreground block mb-1">Role / Name *</label>
        <Input value={form.role || ''} onChange={e => set('role', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Job Title</label>
        <Input value={form.job_title || ''} onChange={e => set('job_title', e.target.value)} className="h-8 text-sm" placeholder="e.g. Senior Engineer" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Team / Department</label>
        <Input value={form.team || ''} onChange={e => set('team', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Annual Salary</label>
        <Input type="number" value={form.base_salary || ''} onChange={e => set('base_salary', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Benefits %</label>
        <Input type="number" value={form.benefits_pct || ''} onChange={e => set('benefits_pct', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Start Date</label>
        <Input type="date" value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">End Date</label>
        <Input type="date" value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Expense Category Link</label>
        <Select value={form.category_link || expOptions[0]?.value || ''} onValueChange={v => set('category_link', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{expOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Scenario</label>
        <Select value={form.scenario || 'base'} onValueChange={v => set('scenario', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{SCENARIOS.map(s => <SelectItem key={s.toLowerCase()} value={s.toLowerCase()}>{s}</SelectItem>)}</SelectContent>
        </Select></div>
    </div>
  );

  if (type === 'tools') return (
    <div className="grid grid-cols-2 gap-3">
      <div><label className="text-xs text-muted-foreground block mb-1">Tool Name *</label>
        <Input value={form.tool_name || ''} onChange={e => set('tool_name', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Category</label>
        <Input value={form.tool_category || ''} onChange={e => set('tool_category', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Monthly Cost</label>
        <Input type="number" value={form.monthly_cost || ''} onChange={e => set('monthly_cost', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Growth % (MoM)</label>
        <Input type="number" value={form.growth_pct || ''} onChange={e => set('growth_pct', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Start Date</label>
        <Input type="date" value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">End Date</label>
        <Input type="date" value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Expense Category Link</label>
        <Select value={form.category_link || expOptions[0]?.value || ''} onValueChange={v => set('category_link', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{expOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Scenario</label>
        <Select value={form.scenario || 'base'} onValueChange={v => set('scenario', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{SCENARIOS.map(s => <SelectItem key={s.toLowerCase()} value={s.toLowerCase()}>{s}</SelectItem>)}</SelectContent>
        </Select></div>
    </div>
  );

  if (type === 'investment') return (
    <div className="grid grid-cols-2 gap-3">
      <div><label className="text-xs text-muted-foreground block mb-1">Investor Name *</label>
        <Input value={form.investor_name || ''} onChange={e => set('investor_name', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Investment Type</label>
        <Select value={form.investment_type || 'Equity'} onValueChange={v => set('investment_type', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{['Equity', 'SAFE', 'Convertible', 'Loan'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Round Name</label>
        <Input value={form.round_name || ''} onChange={e => set('round_name', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Expected Close Date</label>
        <Input type="date" value={form.expected_close_date || ''} onChange={e => set('expected_close_date', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Expected Amount</label>
        <Input type="number" value={form.expected_amount || ''} onChange={e => set('expected_amount', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Currency</label>
        <Select value={form.currency || 'USD'} onValueChange={v => set('currency', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{['USD','GBP','EUR','AUD','CAD','SGD'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><label className="text-xs text-muted-foreground block mb-1">FX Rate</label>
        <Input type="number" step="0.01" value={form.fx_rate || '1'} onChange={e => set('fx_rate', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Status</label>
        <Select value={form.status || 'Planned'} onValueChange={v => set('status', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{['Planned','Committed','Received'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Income Category Link</label>
        <Select value={form.category_link || incOptions[0]?.value || ''} onValueChange={v => set('category_link', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{incOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Scenario</label>
        <Select value={form.scenario || 'base'} onValueChange={v => set('scenario', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{SCENARIOS.map(s => <SelectItem key={s.toLowerCase()} value={s.toLowerCase()}>{s}</SelectItem>)}</SelectContent>
        </Select></div>
    </div>
  );

  if (type === 'income_projection') return (
    <div className="grid grid-cols-2 gap-3">
      <div><label className="text-xs text-muted-foreground block mb-1">Product/Stream *</label>
        <Input value={form.name || ''} onChange={e => set('name', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Market</label>
        <Input value={form.market || ''} onChange={e => set('market', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Starting Customers</label>
        <Input type="number" value={form.starting_customers || ''} onChange={e => set('starting_customers', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">New Customers / Month</label>
        <Input type="number" value={form.monthly_new_customers || ''} onChange={e => set('monthly_new_customers', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Churn % (Monthly)</label>
        <Input type="number" step="0.1" value={form.churn_pct || ''} onChange={e => set('churn_pct', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Price per Customer</label>
        <Input type="number" value={form.price_per_customer || ''} onChange={e => set('price_per_customer', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Sales Lag (months)</label>
        <Input type="number" value={form.sales_lag || ''} onChange={e => set('sales_lag', e.target.value)} className="h-8 text-sm" /></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Income Category Link</label>
        <Select value={form.income_category || incOptions[0]?.value || ''} onValueChange={v => set('income_category', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{incOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><label className="text-xs text-muted-foreground block mb-1">Scenario</label>
        <Select value={form.scenario || 'base'} onValueChange={v => set('scenario', v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{SCENARIOS.map(s => <SelectItem key={s.toLowerCase()} value={s.toLowerCase()}>{s}</SelectItem>)}</SelectContent>
        </Select></div>
    </div>
  );

  return null;
}
